/**
 * OrderFilesPage - Production Files Page
 * 
 * Step 2 of the order setup flow.
 * Contains audio upload, artwork upload, and submit functionality.
 * No product configuration on this page.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { ordersApi, filesApi, artworkJobsApi } from '../lib/api';
import { uploadCustomerArtwork, describeUploadError } from '../lib/uploadCustomerArtwork';
import { formatDate } from '../lib/utils';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { NCBCustomerStatus } from '../components/NCBClearanceSection';
import { MasterFilesCard } from '../components/MasterFilesCard';
import { CustomerMasterRejectionBanner } from '../components/CustomerMasterRejectionBanner';
import { TracklistValidationCard } from '../components/TracklistValidationCard';
import SplitDeliveryCard from '../components/SplitDeliveryCard';
import ShipmentTrackingSection from '../components/ShipmentTrackingSection';
import { TestPressCard } from '../components/TestPressCard';
import { QuoteSection } from '../components/QuoteSection';
import OrderArtworkTemplates from '../components/OrderArtworkTemplates';
import { Button } from '../components/ui/button';
import CountrySelect from '../components/CountrySelect';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Upload,
    Music,
    Send,
    Pencil,
    Disc,
    Palette,
    Download,
    Trash2,
    CheckCircle2,
    Image as ImageIcon,
    FileImage,
    Loader2,
    Eye,
    Truck,
    MapPin,
    Phone,
    AlertTriangle,
    AlertCircle,
    FileText,
    Check,
    Link,
    File,
    FileArchive,
    Lock,
    Plus,
    X,
    Calendar,
    Clock,
    Info,
    RotateCcw
} from 'lucide-react';
import { ChunkedUploader } from '../components/ChunkedUploader';
import { UploadLinksSection } from '../components/UploadLinksSection';
import { MediaDiscIcon } from '../components/MediaDiscIcons';
import { CollapsibleProductionSection } from '../components/CollapsibleProductionSection';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE || '';

// ==================== Partner / Global Discount row ====================

/**
 * PartnerDiscountRow — one commercial line in the Price Summary that
 * surfaces the ``discount_summary`` block already produced by the
 * backend (``services/invoice_basis.py::_build_discount_summary``).
 *
 * NEVER recalculates. The final total shown in Price Summary already
 * has the discount baked into every line total; this row is DERIVED
 * from ``discount_summary.list_subtotal_*`` / ``discount_eur/local``
 * and only ever exists when a partner or global discount actually
 * fired somewhere on the order.
 *
 * Contract:
 *   • Partner win → "Artisten member discount (10%)" — programme name
 *     comes verbatim from the resolver via ``discount_summary``.
 *   • Global win → "Customer discount (X%)" — never labelled as
 *     "Partner Discount".
 *   • Mixed / item override → generic "Discount" label with the
 *     aggregated amount.
 */
function PartnerDiscountRow({ discountSummary, priceData, customerCurrency, exchangeRates }) {
    if (!discountSummary) return null;
    const listEur = discountSummary.list_subtotal_eur;
    const discountEur = discountSummary.discount_eur;
    if (!discountEur || discountEur <= 0) return null;

    // Prefer the summary's own currency + local totals when the
    // backend included them (frozen orders + priced drafts both do).
    // Only fall back to EUR when we have neither.
    const localCurrency = discountSummary.currency || priceData?.currency_local || customerCurrency || 'EUR';
    const symbols = { EUR: '€', DKK: 'kr', SEK: 'kr', NOK: 'kr', GBP: '£', USD: '$' };
    const symbol = symbols[localCurrency] || localCurrency;
    const isEurSummary = localCurrency === 'EUR';

    // Choose amounts:
    //  - EUR customer: use ..._eur fields directly
    //  - Local currency: use ..._local from backend when present,
    //    otherwise fall back to a client-side FX conversion (never a
    //    second discount calc — same amount, different currency).
    let listAmount = isEurSummary ? listEur : discountSummary.list_subtotal_local;
    let discountAmount = isEurSummary ? discountEur : discountSummary.discount_local;
    if (!isEurSummary && (listAmount == null || discountAmount == null)) {
        const rate = exchangeRates?.[localCurrency];
        if (rate) {
            listAmount = listEur * rate;
            discountAmount = discountEur * rate;
        } else {
            listAmount = listEur;
            discountAmount = discountEur;
        }
    }

    const fmt = (n) => {
        const v = Number(n || 0);
        const s = v.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return ['DKK', 'SEK', 'NOK'].includes(localCurrency) ? `${s} ${symbol}` : `${symbol}${s}`;
    };

    // Aggregate percent — safe to derive because backend already
    // computed per-line pct; we surface the max here purely for the
    // "(10%)" tag. When mixed sources fired, drop the pct tag.
    const perLine = discountSummary.lines || [];
    const singleSource = new Set(perLine.map((l) => l.source)).size === 1;
    const dominantPct = singleSource && perLine.length > 0
        ? Math.max(...perLine.map((l) => Number(l.percent) || 0))
        : null;
    const pctLabel = (dominantPct && dominantPct > 0)
        ? ` (${dominantPct}%)`
        : '';

    let label;
    if (discountSummary.source === 'partner' && discountSummary.programme_name) {
        label = `${discountSummary.programme_name} member discount${pctLabel}`;
    } else if (discountSummary.source === 'global') {
        label = `Customer discount${pctLabel}`;
    } else if (discountSummary.source === 'item_override') {
        label = `Item discount${pctLabel}`;
    } else {
        label = 'Discount';
    }

    return (
        <div className="text-xs text-slate-600 space-y-1 border rounded-md bg-emerald-50/40 border-emerald-100 px-3 py-2"
            data-testid="price-summary-discount-row">
            <div className="flex items-center justify-between">
                <span>Subtotal</span>
                <span className="font-mono" data-testid="price-summary-list-subtotal">{fmt(listAmount)}</span>
            </div>
            <div className="flex items-center justify-between text-emerald-800">
                <span className="font-medium" data-testid="price-summary-discount-label">{label}</span>
                <span className="font-mono" data-testid="price-summary-discount-amount">-{fmt(discountAmount)}</span>
            </div>
        </div>
    );
}



// ==================== Artwork File Display Helpers ====================

/**
 * Format file size to human readable format (for artwork files)
 */
const formatArtworkFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Check if file extension supports image preview
 */
const isPreviewableImage = (filename) => {
    if (!filename) return false;
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(ext);
};

/**
 * Get file type icon based on extension
 */
const getFileTypeIcon = (filename) => {
    if (!filename) return FileImage;
    const ext = filename.toLowerCase().split('.').pop();
    
    if (['pdf'].includes(ext)) return FileText;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return FileArchive;
    if (['ai', 'psd', 'eps', 'svg', 'indd'].includes(ext)) return Palette;
    if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'tif'].includes(ext)) return ImageIcon;
    
    return File;
};

/**
 * Get file extension badge color
 */
const getExtensionColor = (filename) => {
    if (!filename) return 'bg-slate-100 text-slate-600';
    const ext = filename.toLowerCase().split('.').pop();
    
    if (['pdf'].includes(ext)) return 'bg-red-100 text-red-700';
    if (['ai'].includes(ext)) return 'bg-orange-100 text-orange-700';
    if (['psd'].includes(ext)) return 'bg-blue-100 text-blue-700';
    if (['zip', 'rar', '7z'].includes(ext)) return 'bg-amber-100 text-amber-700';
    if (['eps', 'svg'].includes(ext)) return 'bg-green-100 text-green-700';
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'bg-purple-100 text-purple-700';
    
    return 'bg-slate-100 text-slate-600';
};

/**
 * ArtworkFileCard - Display a single artwork file with thumbnail or fallback
 */
const ArtworkFileCard = ({ file, compact = false, onDelete = null, canDelete = false, jobLocked = false, replacesFilename = null }) => {
    const canPreview = isPreviewableImage(file.filename);
    const hasUrl = file.url || file.thumbnail_url;
    const showThumbnail = canPreview && hasUrl;
    const FileIcon = getFileTypeIcon(file.filename);
    const ext = file.filename?.split('.').pop()?.toUpperCase() || 'FILE';
    const isLocked = jobLocked || !!file.repro_downloaded_at;
    const showDelete = canDelete && !!onDelete && !isLocked;
    // Revision metadata. Files uploaded via the "Upload Correction" flow on
    // the Artwork Review screen carry `version > 1` and `replaces_file_id`.
    // We surface this so customers (and admins) can see which file is the
    // revised version and which earlier file it superseded.
    const version = file.version || 1;
    const isRevision = version > 1 || !!file.replaces_file_id;
    // PDFs get explicit Preview (inline) + Download buttons. Other file types
    // keep the existing thumbnail-only UX for now (per scope of bug report).
    const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf');
    const handlePdfPreview = async (e) => {
        e.stopPropagation();
        try { await filesApi.previewInline(file.id); }
        catch (err) { toast.error(err?.message || 'Failed to open preview'); }
    };
    const handlePdfDownload = async (e) => {
        e.stopPropagation();
        try { await filesApi.download(file.id); }
        catch { toast.error('Failed to download file'); }
    };
    
    if (compact) {
        // Compact view for grid display
        return (
            <div className={`group relative bg-white rounded-lg border overflow-hidden hover:shadow-sm transition-all ${
                isLocked ? 'border-slate-200 opacity-90' : 'border-slate-200 hover:border-slate-300'
            }`}>
                {/* Revision badge (top-left). Mutually-exclusive corner from
                    the action buttons; uses indigo so it stays distinct from
                    the slate "Locked" pill. */}
                {isRevision && (
                    <div
                        className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-1 bg-indigo-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded shadow-sm"
                        title={replacesFilename ? `Revision — replaces ${replacesFilename}` : 'Revised artwork'}
                        data-testid={`artwork-file-revision-${file.id}`}
                    >
                        v{version}
                    </div>
                )}
                {/* Action buttons (top-right) */}
                {(showDelete || isLocked || isPdf) && (
                    <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1">
                        {isPdf && (
                            <>
                                <button
                                    type="button"
                                    onClick={handlePdfPreview}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded p-1 shadow-sm"
                                    title="Preview PDF"
                                    data-testid={`artwork-pdf-preview-${file.id}`}
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={handlePdfDownload}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded p-1 shadow-sm"
                                    title="Download PDF"
                                    data-testid={`artwork-pdf-download-${file.id}`}
                                >
                                    <Download className="h-3.5 w-3.5" />
                                </button>
                            </>
                        )}
                        {isLocked ? (
                            <span
                                className="inline-flex items-center gap-1 bg-slate-700/90 text-white text-[10px] font-medium px-1.5 py-0.5 rounded"
                                title={`Locked — downloaded for review${file.repro_downloaded_by_name ? ` by ${file.repro_downloaded_by_name}` : ''}`}
                                data-testid={`artwork-file-locked-${file.id}`}
                            >
                                <Lock className="h-3 w-3" />
                                Locked
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onDelete(file); }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded p-1 shadow-sm"
                                title="Delete file"
                                data-testid={`artwork-file-delete-${file.id}`}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                )}

                {/* Thumbnail or Icon */}
                <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
                    {showThumbnail ? (
                        <img 
                            src={file.thumbnail_url || file.url} 
                            alt={file.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div 
                        className={`w-full h-full flex flex-col items-center justify-center ${showThumbnail ? 'hidden' : 'flex'}`}
                    >
                        <FileIcon className="h-8 w-8 text-slate-400 mb-1" />
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${getExtensionColor(file.filename)}`}>
                            {ext}
                        </span>
                    </div>
                </div>
                
                {/* Filename */}
                <div className="p-2 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-700 truncate" title={file.filename}>
                        {file.filename || 'Unnamed file'}
                    </p>
                    {file.size && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                            {formatArtworkFileSize(file.size)}
                        </p>
                    )}
                    {isRevision && replacesFilename && (
                        <p
                            className="text-[10px] text-indigo-600 mt-1 truncate flex items-center gap-1"
                            title={`Replaces ${replacesFilename}`}
                        >
                            <RotateCcw className="h-2.5 w-2.5 flex-shrink-0" />
                            <span className="truncate">Replaces {replacesFilename}</span>
                        </p>
                    )}
                </div>
            </div>
        );
    }
    
    // Full view (list style)
    return (
        <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-all">
            {/* Thumbnail or Icon */}
            <div className="w-12 h-12 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                {showThumbnail ? (
                    <img 
                        src={file.thumbnail_url || file.url} 
                        alt={file.filename}
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div 
                    className={`w-full h-full flex items-center justify-center ${showThumbnail ? 'hidden' : 'flex'}`}
                >
                    <FileIcon className="h-6 w-6 text-slate-400" />
                </div>
            </div>
            
            {/* File info */}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate flex items-center gap-2" title={file.filename}>
                    <span className="truncate">{file.filename || 'Unnamed file'}</span>
                    {isRevision && (
                        <span
                            className="inline-flex items-center bg-indigo-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                            title={replacesFilename ? `Revision — replaces ${replacesFilename}` : 'Revised artwork'}
                            data-testid={`artwork-file-revision-${file.id}`}
                        >
                            v{version}
                        </span>
                    )}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${getExtensionColor(file.filename)}`}>
                        {ext}
                    </span>
                    {file.size && (
                        <span className="text-xs text-slate-400">
                            {formatArtworkFileSize(file.size)}
                        </span>
                    )}
                </div>
                {isRevision && replacesFilename && (
                    <p
                        className="text-xs text-indigo-600 mt-1 flex items-center gap-1"
                        title={`Replaces ${replacesFilename}`}
                    >
                        <RotateCcw className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">Replaces {replacesFilename}</span>
                    </p>
                )}
            </div>
            {isPdf && (
                <div className="flex items-center gap-1 mr-1">
                    <button
                        type="button"
                        onClick={handlePdfPreview}
                        className="text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded p-1.5 transition-colors"
                        title="Preview PDF"
                        data-testid={`artwork-pdf-preview-${file.id}`}
                    >
                        <Eye className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={handlePdfDownload}
                        className="text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded p-1.5 transition-colors"
                        title="Download PDF"
                        data-testid={`artwork-pdf-download-${file.id}`}
                    >
                        <Download className="h-4 w-4" />
                    </button>
                </div>
            )}
            {isLocked ? (
                <span
                    className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded border border-slate-200"
                    title={`Locked — downloaded for review${file.repro_downloaded_by_name ? ` by ${file.repro_downloaded_by_name}` : ''}`}
                    data-testid={`artwork-file-locked-${file.id}`}
                >
                    <Lock className="h-3 w-3" />
                    Locked
                </span>
            ) : showDelete ? (
                <button
                    type="button"
                    onClick={() => onDelete(file)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded p-1.5 transition-colors"
                    title="Delete file"
                    data-testid={`artwork-file-delete-${file.id}`}
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            ) : null}
        </div>
    );
};

/**
 * ArtworkFilesGrid - Display multiple artwork files in a grid
 */
const ArtworkFilesGrid = ({ files, maxDisplay = 6, showAll = false, onDelete = null, canDelete = false, jobLocked = false, allFiles = null }) => {
    if (!files || files.length === 0) return null;
    
    const displayFiles = showAll ? files : files.slice(0, maxDisplay);
    const remainingCount = files.length - maxDisplay;

    // Build a lookup so each card can resolve `replaces_file_id` → filename.
    // We fall back to `files` itself when `allFiles` (which usually includes
    // the superseded originals) isn't provided so the component stays
    // self-contained for legacy callers.
    const lookupSource = Array.isArray(allFiles) ? allFiles : files;
    const fileById = new Map(lookupSource.map((f) => [f.id, f]));

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {displayFiles.map((file, idx) => {
                    const replaced = file.replaces_file_id ? fileById.get(file.replaces_file_id) : null;
                    return (
                        <ArtworkFileCard
                            key={file.id || idx}
                            file={file}
                            compact
                            onDelete={onDelete}
                            canDelete={canDelete}
                            jobLocked={jobLocked}
                            replacesFilename={replaced?.filename || null}
                        />
                    );
                })}
            </div>
            
            {!showAll && remainingCount > 0 && (
                <p className="text-xs text-slate-500 text-center">
                    +{remainingCount} more file(s)
                </p>
            )}
        </div>
    );
};

/**
 * FlowDrivenProductSummary - Renders product configuration from flow selections
 */
function FlowDrivenProductSummary({ order, onEdit, canEdit, onDownloadQuote, isDownloading }) {
    const [flowConfig, setFlowConfig] = useState(null);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [priceData, setPriceData] = useState(null);
    const [priceLoading, setPriceLoading] = useState(false);
    const [customerCurrency, setCustomerCurrency] = useState('EUR');
    const [exchangeRates, setExchangeRates] = useState(null);
    const { token } = useAuth();
    // Language drives the ?lang= query so asset labels re-fetch in
    // the new language when the customer flips the header selector.
    const { language, t } = useTranslation();

    // Fetch flow config and assets
    const [skuLabels, setSkuLabels] = useState({});
    useEffect(() => {
        const fetchFlowData = async () => {
            if (!order?.config_flow_id || !order?.config_selections) {
                setLoading(false);
                return;
            }

            try {
                // Fetch flow config
                const flowRes = await fetch(`${API_URL}/api/product-config/flows/${order.config_flow_id}`);
                if (flowRes.ok) {
                    const flow = await flowRes.json();
                    setFlowConfig(flow);
                }

                // Fetch visual builder assets
                const langQ = language && language !== 'en' ? `&lang=${encodeURIComponent(language)}` : '';
                const assetsRes = await fetch(`${API_URL}/api/visual-builder/assets?active_only=true${langQ}`);
                if (assetsRes.ok) {
                    const assetsData = await assetsRes.json();
                    setAssets(assetsData);
                }

                // Fetch friendly display names for every selected SKU.
                // Cross-cuts all rate cards so DVD/CD/Blu-ray SKUs (which
                // often don't have visual_builder_assets rows) still show
                // "DVD-9 Dual Layer" rather than the raw code "dvd9".
                const allSkus = Object.values(order.config_selections || {}).flat().filter(Boolean);
                if (allSkus.length > 0) {
                    const token = localStorage.getItem('token');
                    const namesRes = await fetch(
                        `${API_URL}/api/sales/sku-display-names?skus=${encodeURIComponent(allSkus.join(','))}`,
                        { headers: { Authorization: `Bearer ${token}` } },
                    );
                    if (namesRes.ok) {
                        setSkuLabels(await namesRes.json());
                    }
                }
            } catch (err) {
                console.error('Failed to load flow data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchFlowData();
    }, [order?.config_flow_id, order?.config_selections, language]);

    // Universal quantity resolver — works for vinyl (vinyl_specs), CD/DVD/Blu-ray
    // legacy simple-media orders (order.quantity) AND new flow-based orders where
    // the wizard stores qty in vinyl_specs regardless of product family.
    const resolvedQuantity = order?.vinyl_specs?.quantity || order?.quantity || 0;

    // Fetch price from backend OR use frozen prices for submitted orders
    useEffect(() => {
        const fetchPrice = async () => {
            if (!order?.id) {
                return;
            }

            // If order has frozen pricing (submitted), display it even when
            // no quantity is stored on the order (legacy CD/DVD/Blu-ray orders).
            const hasFrozenPrice =
                order.order_currency &&
                order.order_total_eur !== undefined &&
                order.order_total_eur !== null;

            // For draft orders we need a quantity to compute a price.
            // Frozen orders skip this guard because the total is already locked.
            if (!hasFrozenPrice && !resolvedQuantity) {
                return;
            }

            // Check if order has frozen pricing (submitted orders)
            if (hasFrozenPrice) {
                // Use frozen pricing from order
                setCustomerCurrency(order.order_currency);
                setPriceData({
                    total: order.order_total_eur,
                    unit_price: order.order_unit_price_eur || 0,
                    frozen: true,
                    frozen_rate: order.exchange_rate_used,
                    frozen_total_local: order.order_total_local,
                    frozen_unit_price_local: order.order_unit_price_local,
                    // Partner Programme / global discount visibility —
                    // read the block the backend already built into the
                    // locked invoice_basis so the frontend never
                    // recomputes discount amounts.
                    discount_summary: order?.invoice_basis?.discount_summary || null,
                });
                // For frozen orders, we use the stored rate directly
                setExchangeRates({ [order.order_currency]: order.exchange_rate_used });
                setPriceLoading(false);
                return;
            }

            setPriceLoading(true);
            try {
                // Get customer currency
                const currencyRes = await fetch(`${API_URL}/api/public/customer-currency`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (currencyRes.ok) {
                    const currencyData = await currencyRes.json();
                    setCustomerCurrency(currencyData.currency || 'EUR');
                }

                // Get exchange rates
                const ratesRes = await fetch(`${API_URL}/api/public/exchange-rates`);
                if (ratesRes.ok) {
                    const ratesData = await ratesRes.json();
                    setExchangeRates(ratesData.rates);
                }

                // Fetch order price from dedicated endpoint
                const priceRes = await fetch(`${API_URL}/api/sales/orders/${order.id}/price`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });

                if (priceRes.ok) {
                    const priceData = await priceRes.json();
                    if (priceData.total) {
                        setPriceData(priceData);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch price:', err);
            } finally {
                setPriceLoading(false);
            }
        };

        fetchPrice();
        // ``deliverySignature`` is derived from the fields the freight
        // reconciler mutates (delivery_mode, delivery_addresses and the
        // resulting auto_addons). Whenever the customer confirms a
        // delivery change — Single↔Split, add/remove a destination —
        // ``setOrder(updatedOrder)`` in ``handleDeliveryUpdate`` swaps
        // the whole order, this signature changes, and the effect re-
        // runs so Price Summary rehydrates from the same
        // ``/api/sales/orders/{id}/price`` endpoint the quote uses. No
        // manual page refresh required.
    }, [
        order?.id, resolvedQuantity, order?.order_currency,
        order?.order_total_eur, token,
        order?.delivery_mode,
        (order?.delivery_addresses || []).length,
        JSON.stringify(order?.auto_addons || []),
    ]);

    // Currency conversion helper - converts EUR to local currency
    const convertAndFormat = (amountEur, currency, rates) => {
        if (!amountEur || currency === 'EUR' || !rates) {
            return `€${amountEur?.toFixed(2) || '0.00'}`;
        }
        
        const rate = rates[currency];
        if (!rate) return `€${amountEur.toFixed(2)}`;
        
        let converted = amountEur * rate;
        
        // Scandinavian rounding (round to nearest 0.05)
        if (['DKK', 'SEK', 'NOK'].includes(currency)) {
            converted = Math.round(converted * 20) / 20;
        } else {
            converted = Math.round(converted * 100) / 100;
        }
        
        const symbols = { EUR: '€', DKK: 'kr', SEK: 'kr', NOK: 'kr', GBP: '£', USD: '$' };
        const symbol = symbols[currency] || currency;
        
        if (['DKK', 'SEK', 'NOK'].includes(currency)) {
            return `${converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
        }
        return `${symbol}${converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Format already-local currency amount (no conversion needed)
    const formatLocalCurrency = (amount, currency) => {
        if (!amount && amount !== 0) return null;
        
        const symbols = { EUR: '€', DKK: 'kr', SEK: 'kr', NOK: 'kr', GBP: '£', USD: '$' };
        const symbol = symbols[currency] || currency || '€';
        
        const formatted = parseFloat(amount).toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        });
        
        if (['DKK', 'SEK', 'NOK'].includes(currency)) {
            return `${formatted} ${symbol}`;
        }
        return `${symbol}${formatted}`;
    };

    // Resolve selection labels from assets — prefer:
    //   1. visual_builder_assets label (vinyl / rich category assets)
    //   2. sales_rate_items display_name (DVD / CD / Blu-ray — no VB assets)
    //   3. raw option_id as last-resort so we never render empty cells
    const resolvedSelections = useMemo(() => {
        if (!flowConfig?.steps || !order?.config_selections) {
            return [];
        }

        return flowConfig.steps.map(step => {
            const selectedOptionIds = order.config_selections[step.step_id] || [];

            const selectedLabels = selectedOptionIds.map(optionId => {
                const asset = assets.find(a =>
                    a.category_id === step.category_id && a.option_id === optionId,
                );
                return asset?.label || skuLabels[optionId] || optionId;
            });

            return {
                stepId: step.step_id,
                label: step.label,
                values: selectedLabels,
                hasSelection: selectedLabels.length > 0,
            };
        }).filter(s => s.hasSelection); // Only show steps with selections
    }, [flowConfig, order?.config_selections, assets, skuLabels]);

    // If no flow data, fall back to legacy vinyl_specs
    const hasFlowData = order?.config_flow_id && order?.config_selections && Object.keys(order.config_selections).length > 0;

    if (loading) {
        return (
            <Card className="mb-6">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                        <Disc className="h-4 w-4" />
                        {t('order.product_configuration', 'Product Configuration')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">{t('order.loading_configuration', 'Loading configuration...')}</span>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Render flow-driven summary
    if (hasFlowData && resolvedSelections.length > 0) {
        // Get quantity — check vinyl_specs (used by all product families in the
        // unified wizard) then fall back to top-level order.quantity for legacy
        // simple-media orders (CD/DVD/Blu-ray).
        const quantity = resolvedQuantity;
        
        return (
            <Card className="mb-6">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                            <Disc className="h-4 w-4" />
                            Product Configuration
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            {canEdit && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={onEdit}
                                    className="text-indigo-600 hover:text-indigo-700"
                                    data-testid="edit-product-btn"
                                >
                                    <Pencil className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 text-sm">
                        {/* Quantity always first */}
                        {quantity > 0 && (
                            <div>
                                <p className="text-xs text-muted-foreground">{t('order.spec.quantity', 'Quantity')}</p>
                                <p className="font-medium">{quantity}</p>
                            </div>
                        )}
                        
                        {/* Flow selections */}
                        {resolvedSelections.map(selection => (
                            <div key={selection.stepId}>
                                <p className="text-xs text-muted-foreground">{selection.label}</p>
                                <p className="font-medium">
                                    {selection.values.join(', ')}
                                </p>
                            </div>
                        ))}
                    </div>
                    
                    {/* Missing-quantity hint for flow-based CD/DVD/Blu-ray
                        orders where the wizard didn't capture a quantity.
                        Nudges the user to click Edit and set one so the
                        price engine can compute a total. */}
                    {!quantity && !priceData && !priceLoading && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="text-xs text-muted-foreground font-medium mb-1">{t('order.price_summary', 'Price Summary')}</p>
                            <p className="text-sm text-slate-500" data-testid="missing-quantity-hint">
                                {t('order.add_quantity_hint', 'Add a production quantity to see the price — click Edit above.')}
                            </p>
                        </div>
                    )}

                    {/* Price Summary Section */}
                    {(priceData || priceLoading) && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                            <div className="flex items-center gap-2 mb-2">
                                <p className="text-xs text-muted-foreground font-medium">{t('order.price_summary', 'Price Summary')}</p>
                                {priceData?.frozen && (
                                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                        {t('order.price_locked', 'Locked at submission')}
                                    </span>
                                )}
                            </div>
                            {priceLoading ? (
                                <div className="flex items-center gap-2 text-slate-500">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="text-sm">{t('order.calculating_price', 'Calculating price...')}</span>
                                </div>
                            ) : priceData?.total ? (
                                <div className="space-y-2">
                                    <PartnerDiscountRow
                                        discountSummary={priceData.discount_summary}
                                        priceData={priceData}
                                        customerCurrency={customerCurrency}
                                        exchangeRates={exchangeRates}
                                    />
                                    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
                                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                                        <div>
                                            <span className="text-2xl font-bold text-slate-900" data-testid="order-unit-price">
                                                {priceData.frozen && priceData.frozen_unit_price_local
                                                    ? formatLocalCurrency(priceData.frozen_unit_price_local, customerCurrency)
                                                    : priceData.total_local != null && priceData.currency_local
                                                        ? formatLocalCurrency(priceData.unit_price_local, priceData.currency_local)
                                                        : convertAndFormat(priceData.unit_price, customerCurrency, exchangeRates)
                                                }
                                            </span>
                                            <span className="text-sm text-slate-500 ml-2">
                                                {priceData.unit_price_is_approx
                                                    ? t('order.approx_per_unit', 'approx. per unit')
                                                    : t('order.per_unit', 'per unit')}
                                            </span>
                                        </div>
                                        <div className="text-sm text-slate-600">
                                            <span className="font-medium" data-testid="order-total-price">
                                                {priceData.frozen && priceData.frozen_total_local 
                                                    ? formatLocalCurrency(priceData.frozen_total_local, customerCurrency)
                                                    : priceData.total_local != null && priceData.currency_local
                                                        ? formatLocalCurrency(priceData.total_local, priceData.currency_local)
                                                        : convertAndFormat(priceData.total, customerCurrency, exchangeRates)
                                                }
                                            </span>
                                            <span className="text-slate-400"> {t('order.total_suffix', 'total')}</span>
                                        </div>
                                    </div>
                                    {onDownloadQuote && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={onDownloadQuote}
                                            disabled={isDownloading}
                                            className="text-slate-600 hover:text-slate-700"
                                            data-testid="download-quote-btn"
                                        >
                                            {isDownloading ? (
                                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                            ) : (
                                                <Download className="h-3 w-3 mr-1" />
                                            )}
                                            {isDownloading
                                                ? t('order.generating', 'Generating...')
                                                : t('order.download_quote', 'Download Quote')}
                                        </Button>
                                    )}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500">{t('order.price_not_available', 'Price not available')}</p>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    // Fallback to legacy vinyl_specs display
    const vinylSpecs = order?.vinyl_specs || {};
    const hasLegacySpecs = vinylSpecs.format || vinylSpecs.quantity;

    if (!hasLegacySpecs) {
        return (
            <Card className="mb-6 border-amber-300 bg-amber-50/60" data-testid="product-config-missing-card">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            Product Configuration required
                        </CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-amber-900">
                        Your order has no product configuration yet — that&rsquo;s why no price is shown.
                        Pick a format, quantity and packaging to unlock pricing and continue.
                    </p>
                    {canEdit && (
                        <Button
                            onClick={onEdit}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="configure-product-cta"
                        >
                            <Pencil className="h-3.5 w-3.5 mr-2" />
                            Configure product
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    // Legacy vinyl_specs display (includes Quick Order bundles)
    const bundleTitle = vinylSpecs._bundle_title;

    // Family-aware field set — vinyl bundles surface press-specific specs
    // (speed / color / weight / test pressings), while CD/DVD/Blu-ray
    // bundles surface disc format + packaging (jacket_style). Falls back
    // to `vinyl` to preserve behaviour for legacy orders that saved a
    // vinyl bundle without ever recording `product_type` on the order.
    const family = (order?.product_type || 'vinyl').toLowerCase();
    const summaryFields = (() => {
        const common = [
            { label: t('order.spec.format', 'Format'), value: vinylSpecs.format },
            { label: t('order.spec.quantity', 'Quantity'), value: vinylSpecs.quantity },
        ];
        if (family === 'vinyl') {
            return [
                ...common,
                { label: t('order.spec.speed', 'Speed'), value: vinylSpecs.speed },
                { label: t('order.spec.color', 'Color'), value: vinylSpecs.color || vinylSpecs.color_mode },
                { label: t('order.spec.weight', 'Weight'), value: vinylSpecs.vinyl_weight_g ? `${vinylSpecs.vinyl_weight_g}g` : (vinylSpecs.vinyl_weight || null) },
                {
                    label: t('order.spec.test_pressings', 'Test Pressings'),
                    value: vinylSpecs.test_pressings !== undefined && vinylSpecs.test_pressings !== null
                        ? (parseInt(vinylSpecs.test_pressings) ? String(vinylSpecs.test_pressings) : t('order.spec.none', 'None'))
                        : null,
                },
            ];
        }
        // CD / DVD / Blu-ray share the same disc-media surface: format +
        // packaging. Extra bundled_options (e.g. barcode_type) render as
        // Title-cased labels automatically.
        const knownKeys = ['format', 'quantity', 'jacket_style'];
        const extras = Object.entries(vinylSpecs)
            .filter(([k, v]) => (
                !k.startsWith('_')
                && !knownKeys.includes(k)
                && v !== null && v !== undefined && v !== ''
                // Filter out vinyl-only pollution keys that may still be
                // present on legacy CD/DVD/Blu-ray orders from before the
                // family-aware fix (see ProductConfigWizard handleBundleSelect).
                && !['speed', 'color', 'color_mode', 'vinyl_weight', 'vinyl_weight_g', 'test_pressings',
                     'sleeve_package', 'inner_sleeve', 'outer_protection', 'printing', 'spine',
                     'center_label', 'runout_engraving', 'engraving_text'].includes(k)
            ))
            .map(([k, v]) => ({
                label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                value: typeof v === 'object' ? JSON.stringify(v) : String(v),
            }));
        return [
            ...common,
            { label: t('order.spec.packaging', 'Packaging'), value: vinylSpecs.jacket_style },
            ...extras,
        ];
    })();

    return (
        <Card className="mb-6">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                            <Disc className="h-4 w-4" />
                            {t('order.product_configuration', 'Product Configuration')}
                        </CardTitle>
                        {bundleTitle && (
                            <p className="text-sm text-emerald-600 mt-1 font-medium">{bundleTitle}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {onDownloadQuote && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onDownloadQuote}
                                disabled={isDownloading}
                                className="text-slate-600 hover:text-slate-700"
                                data-testid="download-quote-btn"
                            >
                                {isDownloading ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                    <Download className="h-3 w-3 mr-1" />
                                )}
                                {isDownloading
                                    ? t('order.generating', 'Generating...')
                                    : t('order.download_quote', 'Download Quote')}
                            </Button>
                        )}
                        {canEdit && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onEdit}
                                className="text-indigo-600 hover:text-indigo-700"
                                data-testid="edit-product-btn"
                            >
                                <Pencil className="h-3 w-3 mr-1" />
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 text-sm">
                    {summaryFields
                        .filter((f) => f.value !== null && f.value !== undefined && f.value !== '')
                        .map((f) => (
                            <div key={f.label} data-testid={`product-summary-${f.label.toLowerCase().replace(/\s+/g, '-')}`}>
                                <p className="text-xs text-muted-foreground">{f.label}</p>
                                <p className="font-medium">{f.value}</p>
                            </div>
                        ))}
                </div>
                
                {/* Price Summary Section for Quick Orders */}
                {(priceData || priceLoading) && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-xs text-muted-foreground mb-2 font-medium">{t('order.price_summary', 'Price Summary')}</p>
                        {priceLoading ? (
                            <div className="flex items-center gap-2 text-slate-500">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-sm">{t('order.calculating_price', 'Calculating price...')}</span>
                            </div>
                        ) : priceData?.total ? (
                            <div className="space-y-2">
                                <PartnerDiscountRow
                                    discountSummary={priceData.discount_summary}
                                    priceData={priceData}
                                    customerCurrency={customerCurrency}
                                    exchangeRates={exchangeRates}
                                />
                                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                                <div>
                                    <span className="text-2xl font-bold text-slate-900" data-testid="order-unit-price">
                                        {priceData.total_local != null && priceData.currency_local
                                            ? formatLocalCurrency(priceData.unit_price_local, priceData.currency_local)
                                            : convertAndFormat(priceData.unit_price, customerCurrency, exchangeRates)
                                        }
                                    </span>
                                    <span className="text-sm text-slate-500 ml-2">
                                        {priceData.unit_price_is_approx
                                            ? t('order.approx_per_unit', 'approx. per unit')
                                            : t('order.per_unit', 'per unit')}
                                    </span>
                                </div>
                                <div className="text-sm text-slate-600">
                                    <span className="font-medium" data-testid="order-total-price">
                                        {priceData.total_local != null && priceData.currency_local
                                            ? formatLocalCurrency(priceData.total_local, priceData.currency_local)
                                            : convertAndFormat(priceData.total, customerCurrency, exchangeRates)
                                        }
                                    </span>
                                    <span className="text-slate-400"> {t('order.total_suffix', 'total')}</span>
                                </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500">{t('order.price_not_available', 'Price not available')}</p>
                        )}
                    </div>
                )}
                {order?.notes && (
                    <div className="mt-4 pt-4 border-t border-slate-200" data-testid="order-production-note">
                        <p className="text-xs text-muted-foreground mb-1">{t('order.production_note_label', 'Note to production team')}</p>
                        <p className="text-sm text-slate-600 whitespace-pre-line">{order.notes}</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * SimpleMediaProductSummary - Renders simple media order details (CD/DVD/Blu-ray)
 * No configurator editing - just displays order info
 */
function SimpleMediaProductSummary({ order }) {
    const { t } = useTranslation();
    const productType = order?.product_type || 'cd';
    
    const PRODUCT_CONFIG = {
        cd:     { label: t('order.cd_production', 'CD Production'),         iconType: 'cd',     color: 'text-blue-600' },
        dvd:    { label: t('order.dvd_production', 'DVD Production'),       iconType: 'dvd',    color: 'text-purple-600' },
        bluray: { label: t('order.bluray_production', 'Blu-ray Production'), iconType: 'bluray', color: 'text-indigo-600' }
    };
    
    const config = PRODUCT_CONFIG[productType] || PRODUCT_CONFIG.cd;
    
    return (
        <Card className="mb-6">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                        <MediaDiscIcon type={config.iconType} size={24} />
                        {config.label}
                    </CardTitle>
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                        <p className="text-xs text-muted-foreground">{t('order.spec.product_type', 'Product Type')}</p>
                        <p className={`font-medium ${config.color}`}>
                            {productType === 'bluray' ? 'Blu-ray' : productType.toUpperCase()}
                        </p>
                    </div>
                    {order?.quantity && (
                        <div>
                            <p className="text-xs text-muted-foreground">{t('order.spec.quantity', 'Quantity')}</p>
                            <p className="font-medium">{order.quantity.toLocaleString()} {t('order.units', 'units')}</p>
                        </div>
                    )}
                    {order?.catalogue_number && (
                        <div>
                            <p className="text-xs text-muted-foreground">{t('order.spec.catalogue_number_short', 'Catalogue No.')}</p>
                            <p className="font-medium">{order.catalogue_number}</p>
                        </div>
                    )}
                </div>
                {order?.notes && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-xs text-muted-foreground mb-1">Notes</p>
                        <p className="text-sm text-slate-600 whitespace-pre-line">{order.notes}</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * ShippingAddressCard - Displays and allows editing of shipping address for the order
 */
function ShippingAddressCard({ order, onShippingUpdate, canEdit }) {
    const { user, activeCompany } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [organization, setOrganization] = useState(null);
    const [loadingOrg, setLoadingOrg] = useState(true);
    
    // Form state
    const [companyName, setCompanyName] = useState('');
    const [attentionTo, setAttentionTo] = useState('');
    const [street, setStreet] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [phone, setPhone] = useState('');
    const [deliveryEmail, setDeliveryEmail] = useState('');
    
    // Load organization for default address (use active company context)
    useEffect(() => {
        const loadOrganization = async () => {
            // Use active company ID for company-scoped data
            const companyId = activeCompany?.id || user?.organization_id;
            if (!companyId) {
                setLoadingOrg(false);
                return;
            }
            
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/organizations/${companyId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setOrganization(data);
                }
            } catch (error) {
                console.error('Failed to load organization:', error);
            } finally {
                setLoadingOrg(false);
            }
        };
        loadOrganization();
    }, [activeCompany?.id, user?.organization_id]);

    // Initialize form with order shipping address or derive from organization
    useEffect(() => {
        if (order?.shipping_address) {
            // Use order's shipping address
            setCompanyName(order.shipping_address.company_name || '');
            setAttentionTo(order.shipping_address.attention_to || '');
            setStreet(order.shipping_address.street || '');
            setPostalCode(order.shipping_address.postal_code || '');
            setCity(order.shipping_address.city || '');
            setCountry(order.shipping_address.country || '');
            setPhone(order.shipping_address.phone || '');
            setDeliveryEmail(order.shipping_address.delivery_email || '');
        }
    }, [order?.shipping_address]);
    
    // Get the default address from organization
    const getDefaultAddress = () => {
        if (!organization) return null;
        
        // First check for explicit shipping address (when shipping_same_as_billing is false)
        if (organization.shipping_same_as_billing === false) {
            // Use saved shipping address if it exists
            if (organization.shipping_address && 
                organization.shipping_address.street && 
                organization.shipping_address.city) {
                return organization.shipping_address;
            }
            return null;
        }
        
        // shipping_same_as_billing is true (or not explicitly set) - use billing address
        // Check if we have a valid billing address with required fields
        const hasBillingAddress = organization.billing_address_line1 && 
                                   organization.billing_city && 
                                   organization.billing_postal_code && 
                                   organization.billing_country;
        
        if (hasBillingAddress) {
            return {
                company_name: organization.name || organization.company_name || '',
                attention_to: organization.contact_person || '',
                street: organization.billing_address_line1 || '',
                postal_code: organization.billing_postal_code || '',
                city: organization.billing_city || '',
                country: organization.billing_country || '',
                phone: organization.contact_phone || '',
                delivery_email: organization.contact_email || organization.billing_email || '',
            };
        }
        
        // No valid default address available
        return null;
    };
    
    // Check if a default address is available (for button visibility/state)
    const hasDefaultAddress = () => {
        if (!organization) return false;
        
        if (organization.shipping_same_as_billing === false) {
            // Need explicit shipping address
            return !!(organization.shipping_address && 
                      organization.shipping_address.street && 
                      organization.shipping_address.city);
        }
        
        // shipping_same_as_billing is true - need valid billing address
        return !!(organization.billing_address_line1 && 
                  organization.billing_city && 
                  organization.billing_postal_code && 
                  organization.billing_country);
    };
    
    const handleUseDefault = async () => {
        const defaultAddr = getDefaultAddress();
        if (!defaultAddr) {
            // Provide more specific error message
            if (organization?.shipping_same_as_billing === false) {
                toast.error('No shipping address configured. Please set one in Billing Profile.');
            } else {
                toast.error('No billing address configured. Please complete your Billing Profile first.');
            }
            return;
        }
        
        // Check if phone is available
        if (!defaultAddr.phone) {
            // Set the values but prompt for phone
            setCompanyName(defaultAddr.company_name || '');
            setAttentionTo(defaultAddr.attention_to || '');
            setStreet(defaultAddr.street || '');
            setPostalCode(defaultAddr.postal_code || '');
            setCity(defaultAddr.city || '');
            setCountry(defaultAddr.country || '');
            setDeliveryEmail(defaultAddr.delivery_email || '');
            setPhone('');
            setIsEditing(true);
            toast.info('Please add a phone number - it\'s required for delivery.');
            return;
        }
        
        // Save directly
        setIsSaving(true);
        try {
            await onShippingUpdate({
                company_name: defaultAddr.company_name || null,
                attention_to: defaultAddr.attention_to || null,
                street: defaultAddr.street,
                postal_code: defaultAddr.postal_code,
                city: defaultAddr.city,
                country: defaultAddr.country,
                phone: defaultAddr.phone,
                delivery_email: defaultAddr.delivery_email || null,
            });
            toast.success('Default shipping address applied');
        } catch (error) {
            toast.error(error.message || 'Failed to apply default address');
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleSave = async () => {
        // Validate required fields
        if (!street || !postalCode || !city || !country || !phone) {
            toast.error('Please fill in all required fields (Street, Postal Code, City, Country, Phone)');
            return;
        }
        
        setIsSaving(true);
        try {
            await onShippingUpdate({
                company_name: companyName || null,
                attention_to: attentionTo || null,
                street,
                postal_code: postalCode,
                city,
                country,
                phone,
                delivery_email: deliveryEmail || null,
            });
            setIsEditing(false);
            toast.success('Shipping address confirmed');
        } catch (error) {
            toast.error(error.message || 'Failed to save shipping address');
        } finally {
            setIsSaving(false);
        }
    };
    
    const hasAddress = order?.shipping_address && order?.shipping_confirmed;
    const shippingAddr = order?.shipping_address;
    
    // Show warning if not confirmed
    const showWarning = !hasAddress && canEdit;
    
    return (
        <Card className={`mb-6 ${showWarning ? 'border-amber-300 bg-amber-50/40' : ''}`} data-testid="shipping-address-card">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                        <Truck className="h-4 w-4" />
                        Shipping Address
                        {hasAddress && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        )}
                    </CardTitle>
                    {canEdit && hasAddress && !isEditing && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsEditing(true)}
                            className="text-indigo-600 hover:text-indigo-700"
                            data-testid="edit-shipping-btn"
                        >
                            <Pencil className="h-3 w-3 mr-1" />
                            Change
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {loadingOrg ? (
                    <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading...
                    </div>
                ) : isEditing ? (
                    /* Editing Form */
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500">Company / Attn</label>
                                <input
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="w-full px-3 py-1.5 border rounded-md text-sm placeholder:text-slate-400"
                                    placeholder={t('order.ph_company', 'Enter company name')}
                                    data-testid="shipping-edit-company"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500">Attention To</label>
                                <input
                                    type="text"
                                    value={attentionTo}
                                    onChange={(e) => setAttentionTo(e.target.value)}
                                    className="w-full px-3 py-1.5 border rounded-md text-sm placeholder:text-slate-400"
                                    placeholder={t('order.ph_attention', 'Enter contact name')}
                                    data-testid="shipping-edit-attention"
                                />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs text-slate-500">Street Address *</label>
                            <input
                                type="text"
                                value={street}
                                onChange={(e) => setStreet(e.target.value)}
                                className="w-full px-3 py-1.5 border rounded-md text-sm placeholder:text-slate-400"
                                placeholder={t('order.ph_street', 'Enter street address')}
                                data-testid="shipping-edit-street"
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500">Postal Code *</label>
                                <input
                                    type="text"
                                    value={postalCode}
                                    onChange={(e) => setPostalCode(e.target.value)}
                                    className="w-full px-3 py-1.5 border rounded-md text-sm placeholder:text-slate-400"
                                    placeholder={t('order.ph_postal', 'Enter postal code')}
                                    data-testid="shipping-edit-postal"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500">City *</label>
                                <input
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="w-full px-3 py-1.5 border rounded-md text-sm placeholder:text-slate-400"
                                    placeholder={t('order.ph_city', 'Enter city')}
                                    data-testid="shipping-edit-city"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500">Country *</label>
                                <CountrySelect
                                    value={country}
                                    onChange={(code) => setCountry(code)}
                                    testid="shipping-edit-country"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500 flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    Phone *
                                </label>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full px-3 py-1.5 border rounded-md text-sm placeholder:text-slate-400"
                                    placeholder={t('order.ph_phone', 'Enter phone number')}
                                    data-testid="shipping-edit-phone"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500">Delivery Email</label>
                                <input
                                    type="email"
                                    value={deliveryEmail}
                                    onChange={(e) => setDeliveryEmail(e.target.value)}
                                    className="w-full px-3 py-1.5 border rounded-md text-sm placeholder:text-slate-400"
                                    placeholder={t('order.ph_delivery_email', 'Enter delivery email')}
                                    data-testid="shipping-edit-email"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                            <Button
                                size="sm"
                                onClick={handleSave}
                                disabled={isSaving}
                                data-testid="shipping-save-btn"
                            >
                                {isSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                Confirm Address
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : hasAddress ? (
                    /* Display confirmed address */
                    <div className="text-sm">
                        <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                            <div>
                                {shippingAddr.company_name && (
                                    <p className="font-medium">{shippingAddr.company_name}</p>
                                )}
                                {shippingAddr.attention_to && (
                                    <p className="text-slate-600">Attn: {shippingAddr.attention_to}</p>
                                )}
                                <p>{shippingAddr.street}</p>
                                <p>{shippingAddr.postal_code} {shippingAddr.city}</p>
                                <p>{shippingAddr.country}</p>
                                <p className="flex items-center gap-1 mt-1 text-slate-600">
                                    <Phone className="h-3 w-3" />
                                    {shippingAddr.phone}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* No address - show options */
                    <div className="space-y-3">
                        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-amber-800">
                                    Shipping address required
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    Please confirm where this order should be delivered before submitting.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleUseDefault}
                                disabled={isSaving || loadingOrg}
                                data-testid="use-default-shipping-btn"
                                title={!hasDefaultAddress() ? 'No default address configured in Billing Profile' : 'Use your default shipping address'}
                            >
                                {isSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                Use Default Address
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setIsEditing(true)}
                                data-testid="enter-shipping-btn"
                            >
                                Enter Different Address
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// Artwork Job Status Badge Component (customer-friendly)
const ArtworkJobStatusBadge = ({ status, artworkStatus }) => {
    // Per-file artwork review status takes priority
    if (artworkStatus === 'rejected') {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700" data-testid="artwork-job-status-badge">
                Revision Needed
            </span>
        );
    }
    if (artworkStatus === 'approved') {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700" data-testid="artwork-job-status-badge">
                Approved
            </span>
        );
    }
    
    const statusConfig = {
        'Open': { label: 'Uploaded', className: 'bg-blue-100 text-blue-700' },
        'Assigned': { label: 'In Review', className: 'bg-amber-100 text-amber-700' },
        'In_Progress': { label: 'In Review', className: 'bg-amber-100 text-amber-700' },
        'Awaiting_Approval': { label: 'Needs Your Approval', className: 'bg-purple-100 text-purple-700' },
        'Approved': { label: 'Approved', className: 'bg-emerald-100 text-emerald-700' },
        'Completed': { label: 'Completed', className: 'bg-emerald-100 text-emerald-700' },
    };
    
    const config = statusConfig[status] || { label: 'Not started', className: 'bg-slate-100 text-slate-500' };
    
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`} data-testid="artwork-job-status-badge">
            {config.label}
        </span>
    );
};

// Helper to format file size
const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function OrderFilesPage() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    
    const [order, setOrder] = useState(null);
    const [files, setFiles] = useState([]);
    const [artworkJob, setArtworkJob] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Upload state
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [uploadCategory, setUploadCategory] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Artwork upload state
    const [isArtworkUploadDialogOpen, setIsArtworkUploadDialogOpen] = useState(false);
    const [artworkUploadFiles, setArtworkUploadFiles] = useState([]);
    const [isUploadingArtwork, setIsUploadingArtwork] = useState(false);
    const [artworkUploadProgress, setArtworkUploadProgress] = useState(null); // 0-100 or null
    const artworkInputRef = useRef(null);
    
    // Displayed artwork files (from repro job or order files)
    const [displayArtworkFiles, setDisplayArtworkFiles] = useState([]);

    // Collapsible-section UI state for the Production Files sections
    // (Master Files, Tracklist Validation, Artwork, Upload Links). These
    // are purely presentational — no upload, validation, or business
    // logic changes. Each control increments its own counter so any
    // wrapped section can react exactly once per click, even if the
    // user has since toggled the section manually.
    const [expandAllSignal, setExpandAllSignal] = useState(0);
    const [collapseAllSignal, setCollapseAllSignal] = useState(0);
    // Per-section open signal bumped by the hash deep-link handler
    // below. Added on top of ``expandAllSignal`` when passed as
    // ``openSignal`` on the target CollapsibleProductionSection so the
    // matching card is guaranteed to be expanded when the customer
    // lands via ``#artwork`` / ``#master`` / ``#test-press`` — even if
    // the section's own ``defaultOpen`` resolved to false (e.g. the
    // artwork summary is already marked complete).
    const [sectionOpenBump, setSectionOpenBump] = useState({ 'test-press': 0, 'master-files': 0, artwork: 0 });

    // Keep the Master Files section expanded whenever a master upload is
    // actively uploading / finalizing. <MasterFilesCard> reports its
    // aggregate active-upload state here; we force the section open on the
    // rising edge (false → true) by bumping its open signal. We never force
    // it closed, so the customer can still collapse it manually and the
    // 2.5s completed-entry removal can't collapse it.
    const masterUploadsActiveRef = useRef(false);
    const handleMasterUploadsActiveChange = (active) => {
        if (active && !masterUploadsActiveRef.current) {
            setSectionOpenBump((prev) => ({ ...prev, 'master-files': (prev['master-files'] || 0) + 1 }));
        }
        masterUploadsActiveRef.current = active;
    };
    
    // Quote download state
    const [isDownloadingQuote, setIsDownloadingQuote] = useState(false);
    
    // Catalogue number inline editing
    const [editCatalogueNumber, setEditCatalogueNumber] = useState('');
    const [isSavingCatalogueNumber, setIsSavingCatalogueNumber] = useState(false);
    // Inline-editable order detail fields. Each field is debounced via onBlur
    // → handleSaveOrderField helper below. Keeping local edit state separate
    // from `order` prevents the page from re-rendering on every keystroke
    // while still keeping the persisted version as the source of truth.
    const [editTitle, setEditTitle] = useState('');
    const [editArtist, setEditArtist] = useState('');
    const [editPoNumber, setEditPoNumber] = useState('');
    const [editRequestedDelivery, setEditRequestedDelivery] = useState('');
    const [savingField, setSavingField] = useState(null); // 'title' | 'artist' | 'po_number' | 'requested_delivery_date'

    // Organization state (for account type + billing checks)
    const [organization, setOrganization] = useState(null);

    // Payment status state (for Stripe redirect)
    const [paymentStatus, setPaymentStatus] = useState(null); // null | 'polling' | 'paid' | 'failed'

    // Tabbed layout — split "Order details" (gates submission) from
    // "Production files" (uploadable while still in draft). Default to
    // 'details' so customers see the required-to-submit checklist first.
    const [activeTab, setActiveTab] = useState('details');
    // Honour hash deep-links from the Customer Action Center:
    //   #test-press → Test Press card (Order details tab)
    //   #master     → Master Files card (Production files tab)
    //   #artwork    → Artwork section (Production files tab)
    // Each switches to the correct tab AND bumps the matching section's
    // open signal before scrolling so the card is already expanded when
    // the customer lands. Reuses whatever ``id="…"`` anchor the owning
    // component carries.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const hash = window.location.hash;
        const KNOWN = {
            '#test-press': { id: 'test-press', tab: 'details' },
            '#master':     { id: 'master-files', tab: 'files' },
            '#artwork':    { id: 'artwork', tab: 'files' },
        };
        const target = KNOWN[hash];
        if (!target) return;
        setActiveTab(target.tab);
        // Force the matching section open — the collapsible card
        // picks this up via its ``openSignal`` prop (see each section
        // render below). Done exactly once per hash navigation so the
        // customer can still collapse the card after landing.
        setSectionOpenBump((prev) => ({ ...prev, [target.id]: (prev[target.id] || 0) + 1 }));
        const scrollTo = () => {
            const el = document.getElementById(target.id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return true;
            }
            return false;
        };
        // Try a few times — the card renders after the fetch resolves
        // AND after the tab switch commits (~ one frame).
        let tries = 0;
        const timer = setInterval(() => {
            if (scrollTo() || ++tries >= 20) clearInterval(timer);
        }, 150);
        return () => clearInterval(timer);
    }, []);

    // Load organization for billing/account type
    useEffect(() => {
        const loadOrg = async () => {
            const orgId = user?.active_company_id || user?.organization_id;
            if (!orgId) return;
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/organizations/${orgId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setOrganization(data);
                }
            } catch (e) {
                console.error('Failed to load organization for payment check:', e);
            }
        };
        loadOrg();
    }, [user]);

    // Check for Stripe redirect params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        const paymentParam = params.get('payment');
        
        if (sessionId && paymentParam === 'success') {
            setPaymentStatus('polling');
            pollPaymentStatus(sessionId, 0);
            // Clean URL params
            window.history.replaceState({}, '', window.location.pathname);
        } else if (paymentParam === 'cancelled') {
            toast.info(t('order.payment_cancelled', 'Payment was cancelled. Your order is still saved.'));
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [orderId]);

    const pollPaymentStatus = async (sessionId, attempts) => {
        const maxAttempts = 8;
        const pollInterval = 2500;
        
        if (attempts >= maxAttempts) {
            setPaymentStatus('failed');
            toast.error(t('order.payment_timeout', 'Payment status check timed out. Please refresh the page.'));
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_URL}/api/orders/${orderId}/payment-status?session_id=${sessionId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error('Failed to check payment status');
            
            const data = await response.json();
            
            if (data.payment_status === 'paid') {
                setPaymentStatus('paid');
                toast.success(t('order.payment_success', 'Payment successful! Your order is now being processed.'));
                // Refresh order data to get updated status
                fetchData();
                return;
            } else if (data.status === 'expired') {
                setPaymentStatus('failed');
                toast.error(t('order.payment_expired', 'Payment session expired. Please try again.'));
                return;
            }
            
            // Still pending, continue polling
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            console.error('Payment poll error:', error);
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        }
    };

    useEffect(() => {
        fetchData();
    }, [orderId]);

    const fetchData = async () => {
        try {
            const [orderRes, filesRes, artworkJobRes] = await Promise.all([
                ordersApi.getOne(orderId),
                ordersApi.getFiles(orderId),
                artworkJobsApi.getOrderJob(orderId).catch(() => ({ data: null })),
            ]);
            
            setOrder(orderRes.data);
            setFiles(filesRes.data);
            setArtworkJob(artworkJobRes.data);
            
            // If no artwork job, check if there are artwork files from order files (draft uploads)
            if (!artworkJobRes.data) {
                const orderArtworkFiles = (filesRes.data || []).filter(f => f.category === 'artwork');
                setDisplayArtworkFiles(orderArtworkFiles);
            } else {
                setDisplayArtworkFiles(artworkJobRes.data.artwork_files || []);
            }
            
            setEditCatalogueNumber(orderRes.data.catalogue_number || '');
            setEditTitle(orderRes.data.title || '');
            setEditArtist(orderRes.data.artist || '');
            setEditPoNumber(orderRes.data.customer_po_number || '');
            setEditRequestedDelivery(orderRes.data.requested_delivery_date || '');
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error(t('order.access_denied', 'Access denied'));
            } else {
                toast.error(t('order.load_error', 'Failed to load order details'));
            }
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    // Handle catalogue number inline save
    const handleSaveCatalogueNumber = async (newValue) => {
        // Don't save if unchanged
        if (newValue === (order?.catalogue_number || '')) return;
        
        setIsSavingCatalogueNumber(true);
        try {
            const response = await ordersApi.update(orderId, {
                catalogue_number: newValue || null,
            });
            setOrder(response.data);
            toast.success(t('order.catalogue_saved', 'Catalogue number updated'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('order.catalogue_save_error', 'Failed to update catalogue number'));
            // Revert on error
            setEditCatalogueNumber(order?.catalogue_number || '');
        } finally {
            setIsSavingCatalogueNumber(false);
        }
    };

    // Generic inline-save for editable order fields exposed on this page.
    // `field`: server-side key (title / artist / customer_po_number /
    // requested_delivery_date). The pre-edit value comes from `order` so we
    // skip the API call when nothing changed.
    const handleSaveOrderField = async (field, newValue) => {
        const current = order?.[field] || '';
        const trimmed = (newValue ?? '').trim();
        if (trimmed === (current || '')) return;
        if (field === 'title' && !trimmed) {
            toast.error(t('order.title_required', 'Title is required'));
            // revert local state to persisted value
            setEditTitle(current || '');
            return;
        }
        setSavingField(field);
        try {
            const response = await ordersApi.update(orderId, {
                [field]: trimmed || null,
            });
            setOrder(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || t('common.save_error', 'Failed to save'));
            // revert local state on error
            if (field === 'title') setEditTitle(current || '');
            else if (field === 'artist') setEditArtist(current || '');
            else if (field === 'customer_po_number') setEditPoNumber(current || '');
            else if (field === 'requested_delivery_date') setEditRequestedDelivery(current || '');
        } finally {
            setSavingField(null);
        }
    };

    const handleUploadFile = async () => {
        if (!selectedFile || !uploadCategory) return;
        
        setIsUploading(true);
        try {
            const response = await filesApi.upload(orderId, uploadCategory, selectedFile);
            setFiles([response.data, ...files]);
            setIsUploadDialogOpen(false);
            setSelectedFile(null);
            setUploadCategory('');
            toast.success(t('order.file_uploaded', 'File uploaded'));
            
            // Refresh order data after file upload
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('order.file_upload_error', 'Failed to upload file'));
        } finally {
            setIsUploading(false);
        }
    };

    // Artwork upload handlers
    const handleArtworkFilesSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setArtworkUploadFiles(prev => [...prev, ...files]);
        }
    };
    
    const removeArtworkFile = (index) => {
        setArtworkUploadFiles(prev => prev.filter((_, i) => i !== index));
    };
    
    const handleArtworkUpload = async () => {
        if (artworkUploadFiles.length === 0) {
            toast.error(t('order.select_files', 'Please select files to upload'));
            return;
        }
        
        setIsUploadingArtwork(true);
        setArtworkUploadProgress(0);
        try {
            // Routes via chunked-S3 multipart for any file > 40 MB so
            // we don't choke on the Kubernetes ingress body limit.
            const response = await uploadCustomerArtwork(
                orderId, artworkUploadFiles,
                ({ phase, fileIndex, totalFiles, percent }) => {
                    // Combine per-file progress into an overall %.
                    if (phase === 'chunked') {
                        const overall = Math.round(
                            ((fileIndex + percent / 100) / totalFiles) * 100,
                        );
                        setArtworkUploadProgress(overall);
                    } else {
                        setArtworkUploadProgress(percent);
                    }
                },
            );

            toast.success(response.job_created
                ? `Artwork uploaded! ${response.files.length} file(s) added.`
                : `${response.files.length} file(s) added to artwork.`
            );
            
            // Update artworkJob state immediately with the response
            if (response.job) {
                setArtworkJob(response.job);
            }
            
            // Close dialog and reset
            setIsArtworkUploadDialogOpen(false);
            setArtworkUploadFiles([]);
            
            // Also refresh to ensure consistency
            fetchData();
        } catch (error) {
            // Show the real backend error so the customer/admin can
            // act on it (413, 504, server-side validation, etc.).
            toast.error(describeUploadError(error), { duration: 8000 });
        } finally {
            setIsUploadingArtwork(false);
            setArtworkUploadProgress(null);
        }
    };

    // Determine if this is a private account (requires Stripe payment)
    const isPrivateAccount = organization?.type === 'private';
    const billingComplete = organization?.billing_profile_complete === true;
    const billingStatus = organization?.billing_status;

    const handleSubmitOrder = async () => {
        // Validate required fields
        if (!order.title || order.title.trim() === '') {
            toast.error(t('order.title_required_submit', 'Please set an order title before submitting'));
            navigate(`/orders/${orderId}/product?edit=true`);
            return;
        }
        
        if (!order.catalogue_number && !editCatalogueNumber.trim()) {
            toast.error(t('order.catalogue_required_submit', 'Catalogue number is required to submit. Please add it above.'));
            const input = document.getElementById('catalogue-number');
            if (input) input.focus();
            return;
        }
        
        // If catalogue number was just edited but not saved yet, save it first
        if (editCatalogueNumber.trim() && editCatalogueNumber !== order.catalogue_number) {
            await handleSaveCatalogueNumber(editCatalogueNumber.trim());
        }
        
        // Private account → Stripe payment flow
        if (isPrivateAccount) {
            if (!billingComplete) {
                toast.error(t('order.billing_required', 'Please complete your billing profile before proceeding to payment.'));
                navigate('/settings/billing');
                return;
            }
            
            setIsSubmitting(true);
            try {
                const response = await fetch(`${API_URL}/api/orders/${orderId}/checkout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        origin_url: window.location.origin
                    })
                });
                
                if (!response.ok) {
                    const err = await response.json();
                    throw new Error(err.detail || 'Failed to create payment session');
                }
                
                const data = await response.json();
                // Redirect to Stripe Checkout
                window.location.href = data.checkout_url;
            } catch (error) {
                toast.error(error.message || t('order.payment_init_error', 'Failed to initiate payment'));
                setIsSubmitting(false);
            }
            return;
        }
        
        // Business account → normal submit flow
        if (!window.confirm(t('order.submit_confirm', "Submit this order? You won't be able to edit it after submission."))) return;
        
        setIsSubmitting(true);
        try {
            const response = await ordersApi.submit(orderId);
            toast.success(t('order.submit_success', 'Order submitted successfully! Your order is now being processed.'), {
                duration: 4000
            });
            setTimeout(() => {
                navigate('/dashboard');
            }, 1500);
        } catch (error) {
            toast.error(error.response?.data?.detail || t('order.submit_error', 'Failed to submit order'));
            setIsSubmitting(false);
        }
    };

    const handleDownloadFile = async (file) => {
        try {
            const result = await filesApi.download(file.id);
            // If it's a local file (blob returned), handle download manually
            if (!result.isS3 && result.data) {
                const url = window.URL.createObjectURL(result.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', file.original_filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            // S3 files are handled by opening in new tab via filesApi.download
        } catch (error) {
            toast.error(t('order.download_error', 'Failed to download file'));
        }
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm(t('order.delete_file_confirm', 'Are you sure you want to delete this file?'))) return;
        
        try {
            await filesApi.delete(fileId);
            setFiles(files.filter(f => f.id !== fileId));
            toast.success(t('order.file_deleted', 'File deleted'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('order.file_delete_error', 'Failed to delete file'));
        }
    };

    const handleDeleteCustomerArtworkFile = async (file) => {
        if (!file?.id) return;
        const lockedStatuses = ['In_Progress', 'Awaiting_Approval', 'Approved', 'Completed'];
        if (lockedStatuses.includes(artworkJob?.status)) {
            toast.error(t('order.artwork_locked_started', 'Locked — repro partner has already started work on this artwork'));
            return;
        }
        if (file.repro_downloaded_at) {
            toast.error(t('order.artwork_locked_downloaded', 'Locked — already downloaded for review'));
            return;
        }
        if (!window.confirm(t('order.artwork_delete_confirm', 'Delete artwork file "{name}"? This cannot be undone.', { name: file.filename }))) return;
        try {
            await artworkJobsApi.deleteCustomerArtworkFile(orderId, file.id);
            toast.success(t('order.artwork_deleted', 'Artwork file deleted'));
            // Optimistic local update — also refetch authoritative data
            setDisplayArtworkFiles(prev => prev.filter(f => f.id !== file.id));
            fetchData();
        } catch (error) {
            const msg = error.response?.data?.detail || t('order.artwork_delete_error', 'Failed to delete artwork file');
            toast.error(msg);
        }
    };

    // Handle quote PDF download
    const handleDownloadQuote = async () => {
        if (!order?.id) return;
        
        setIsDownloadingQuote(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/orders/${order.id}/quote-pdf`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                // Clone the response before reading to avoid "body stream already read" error
                const errorText = await response.text();
                let errorMessage = 'Failed to generate quote';
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.detail || errorMessage;
                } catch {
                    // If not JSON, use the raw text or default message
                    if (errorText) errorMessage = errorText;
                }
                throw new Error(errorMessage);
            }
            
            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `Owntune_Quote_${order.order_number || order.id}.pdf`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            // Download the PDF
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success(t('order.quote_downloaded', 'Quote downloaded successfully'));
        } catch (error) {
            console.error('Quote download error:', error);
            toast.error(error.message || t('order.quote_download_error', 'Failed to download quote'));
        } finally {
            setIsDownloadingQuote(false);
        }
    };

    // Guests reach this page via a scoped upload link — they may upload
    // files in their granted categories but must NEVER mutate the order's
    // metadata (title / artist / catalogue / PO / delivery).
    const isGuestUser = user?.role === 'guest';
    const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
    const canEdit = !isGuestUser && (order?.status === 'Draft' || order?.status === 'awaiting_payment');
    // Order *detail* fields (title / artist / catalogue / PO / requested
    // delivery) stay editable throughout the quote lifecycle — while the
    // quote is pending ("Quote") and after it has been sent to the customer
    // ("Quoted") — and lock only once the customer accepts the quote
    // (status "Submitted" and beyond). This is intentionally broader than
    // `canEdit`, which also gates the priced product configuration (that
    // must stay frozen to the quoted price).
    const canEditOrderDetails = !isGuestUser && (
        canEdit || order?.status === 'Quote' || order?.status === 'Quoted'
    );
    const canUploadFiles = order?.status && !['In Progress', 'Completed', 'Paid'].includes(order.status);
    const canDeleteFiles = canUploadFiles;

    // Audio files by side / tracklist / legacy bucket are now derived inside <MasterFilesCard>.
    // Only artwork-related derived state stays here.

    const hasArtwork = artworkJob !== null;
    const hasShippingConfirmed = order?.shipping_confirmed === true;

    // Does this order have active carrier/shipment tracking from the
    // Inbound Hub? When yes, we hoist the Shipment Tracking card to the
    // TOP of both tabs so a customer landing on the page sees the
    // tracker before any product/order configuration. When there's no
    // tracking yet, the component renders ``null`` and the page keeps
    // its original layout (nothing visibly moves).
    const hasActiveShipmentTracking = Array.isArray(order?.shipment_tracking)
        && order.shipment_tracking.some(r => r?.carrier_tracking?.normalized_status);

    // What is still missing before the customer can submit? Used to drive
    // the "Order details" tab badge (e.g. "2 missing") and the inline
    // checklist inside the tab. Reads from BOTH the persisted order doc
    // and the inline edit state so the badge reacts immediately when the
    // customer types — they shouldn't have to blur a field to see the
    // count drop.
    //
    // Each item carries both a human label AND a stable ``key`` the
    // "Still missing: …" footer uses to render a clickable link that
    // scrolls to / navigates to where the missing data can be filled
    // in. The badge count still works because the array length is
    // unchanged.
    const submissionMissingItems = useMemo(() => {
        if (!order) return [];
        const items = [];
        const persistedTitle = (order.title || editTitle || '').trim();
        if (!persistedTitle) items.push({ key: 'title', label: t('order.missing.title', 'Title') });
        const persistedCat = (order.catalogue_number || editCatalogueNumber || '').trim();
        if (!persistedCat) items.push({ key: 'catalogue_number', label: t('order.missing.catalogue_number', 'Catalogue number') });
        // Product configuration gate. An order is "unconfigured" when
        // none of the pricing inputs are present:
        //   * `config_flow_id` — Visual Builder flow was completed
        //   * `vinyl_specs`     — vinyl format+qty stored (also used as
        //                          the generic specs blob for flow-based
        //                          CD/DVD/Blu-ray)
        //   * `quantity`        — top-level qty on legacy simple-media
        //                          orders (SimpleMediaOrderPage)
        // Without any of these the invoice basis has nothing to price
        // against, so the Product Configuration section on the Order
        // Details tab stays blank ("No product configuration saved yet.").
        // Previously the missing-items footer only mentioned Shipping,
        // which left customers wondering why no price appeared —
        // fixed by exposing it here so both the "N missing" badge and
        // the inline "Still missing: …" line surface it explicitly.
        const hasConfig = (
            !!order.config_flow_id
            || (order.vinyl_specs && (order.vinyl_specs.format || order.vinyl_specs.quantity || order.vinyl_specs._bundle_id))
            || (order.quantity && Number(order.quantity) > 0)
        );
        if (!hasConfig) items.push({ key: 'product_configuration', label: t('order.missing.product_configuration', 'Product configuration') });
        // Billing profile gate — apply to EVERY account. The customer
        // dashboard used to only flag this for `type=='private'` orgs,
        // which meant new signups whose placeholder org still had
        // ``type=null`` slipped through and reached an apparently
        // "Ready to submit" state, only for the backend to reject the
        // submission with "Billing profile incomplete – Missing:
        // Account type". Same rule now applies to partner and normal
        // signups so the UI never shows a green light before Billing
        // Profile is done.
        if (organization && organization.billing_profile_complete !== true) {
            items.push({ key: 'billing_profile', label: t('order.missing.billing_profile', 'Billing profile') });
        }
        if (order.shipping_confirmed !== true) items.push({ key: 'shipping_address', label: t('order.missing.shipping_address', 'Shipping address') });
        return items;
    }, [order, editTitle, editCatalogueNumber, organization, t]);

    // Compact summaries for the collapsible production-file section
    // headers. Pure UI derivations — do not touch upload / validation
    // logic. Categories mirror what MasterFilesCard renders internally
    // (per-disc slots + legacy vinyl side buckets + tracklist file).
    const masterFilesSummary = useMemo(() => {
        if (!order) return { discCount: 0, filesCount: 0, status: 'pending', text: 'No files yet' };
        const slots = order.master_slots || [];
        const productType = order.product_type;
        const isCd = productType === 'cd';
        const isDvd = productType === 'dvd';
        const isBluray = productType === 'bluray';
        const isDiscOrder = isCd || isDvd || isBluray;

        // Count uploaded master files across every category that
        // MasterFilesCard reads from.
        const masterCategories = new Set();
        slots.forEach((s) => {
            masterCategories.add(`master_slot_${s.id}`);
            masterCategories.add(`master_slot_${s.id}_layer0`);
            masterCategories.add(`master_slot_${s.id}_layer1`);
        });
        // Legacy vinyl side buckets + tracklist sidecar.
        ['side_a', 'side_b', 'tracklist'].forEach((c) => masterCategories.add(c));

        const uploaded = (files || []).filter((f) => masterCategories.has(f.category));
        const filesCount = uploaded.length;
        const discCount = isDiscOrder ? Math.max(slots.length, 1) : 0;

        // Consider "complete" only when every disc/slot has at least
        // one file. For vinyl we require any master file at all.
        let complete = false;
        let filledDiscs = 0;
        if (isDiscOrder && slots.length > 0) {
            filledDiscs = slots.filter((s) => {
                return uploaded.some(
                    (f) => f.category === `master_slot_${s.id}`
                        || f.category === `master_slot_${s.id}_layer0`
                        || f.category === `master_slot_${s.id}_layer1`,
                );
            }).length;
            complete = filledDiscs === slots.length;
        } else if (!isDiscOrder) {
            complete = filesCount > 0;
        }

        const parts = [];
        if (discCount > 0) parts.push(`${filledDiscs}/${discCount} disc${discCount === 1 ? '' : 's'}`);
        parts.push(`${filesCount} file${filesCount === 1 ? '' : 's'} uploaded`);

        // Explicit missing / partial / complete state so the collapsed
        // header communicates progress without expanding. "Partial" only
        // applies to multi-disc orders where some (but not all) discs are
        // filled.
        let status;
        let label;
        if (complete) {
            status = 'complete';
            label = 'Complete';
        } else if (filesCount > 0) {
            status = 'warning';
            label = 'Partial';
        } else {
            status = 'pending';
            label = 'Missing';
        }

        return {
            discCount,
            filledDiscs,
            filesCount,
            status,
            label,
            complete,
            text: parts.join(' · '),
        };
    }, [order, files]);

    const artworkSummary = useMemo(() => {
        const activeFiles = (displayArtworkFiles || []).filter((f) => !f?.superseded);
        const filesCount = activeFiles.length;
        const rejected = artworkJob?.artwork_status === 'rejected';
        const approved = artworkJob?.artwork_status === 'approved' || artworkJob?.status === 'Approved' || artworkJob?.status === 'Completed';
        let status = 'pending';
        let text = 'No artwork uploaded';
        let label = 'Missing';
        if (rejected) {
            status = 'warning';
            text = `${filesCount} file${filesCount === 1 ? '' : 's'} · needs revision`;
            label = 'Needs revision';
        } else if (approved) {
            status = 'complete';
            text = `${filesCount} file${filesCount === 1 ? '' : 's'} · approved`;
            label = 'Complete';
        } else if (filesCount > 0) {
            status = artworkJob ? 'pending' : 'complete';
            text = `${filesCount} file${filesCount === 1 ? '' : 's'} uploaded`;
            label = artworkJob ? 'Uploaded' : 'Complete';
        }
        return { filesCount, status, complete: status === 'complete', text, label };
    }, [displayArtworkFiles, artworkJob]);

    // Production-files readiness for the tab badge. Reuses the SAME
    // master/artwork completion rules the sections render
    // (masterFilesSummary / artworkSummary) so the badge can never
    // disagree with the tab content. Purely informational — it NEVER
    // blocks submission (see submissionMissingItems, which intentionally
    // omits production files so drafts stay submittable without them).
    //   'complete'    → every applicable required file satisfies its rule
    //   'missing'     → a required artwork/master file has NOT been provided
    //   'in_progress' → required files provided but still pending / partial
    const productionFilesStatus = useMemo(() => {
        if (!order) return 'in_progress';
        // Reorders reusing the catalogue's approved assets need no new
        // files for that category.
        const isReorderOrder = order.order_type === 'reorder';
        const artworkNotNeeded = isReorderOrder || order.reusable_artwork === true;
        const masterNotNeeded = isReorderOrder || order.reusable_master === true;

        const masterComplete = masterNotNeeded || masterFilesSummary.complete;
        const artworkComplete = artworkNotNeeded || artworkSummary.complete;
        if (masterComplete && artworkComplete) return 'complete';

        const masterProvided = masterNotNeeded || masterFilesSummary.filesCount > 0 || order.has_audio_master === true;
        const artworkProvided = artworkNotNeeded || artworkSummary.filesCount > 0 || !!order.artwork_status;
        if (!masterProvided || !artworkProvided) return 'missing';

        return 'in_progress';
    }, [order, masterFilesSummary, artworkSummary]);

    const isCdProduct = order?.product_type === 'cd';

    // Handle delivery update (single or split)
    const handleDeliveryUpdate = async (deliveryPayload) => {
        const token = localStorage.getItem('token');
        const body = { shipping_confirmed: true, ...deliveryPayload };
        const response = await fetch(`${API_URL}/api/orders/${order.id}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.detail || 'Failed to update delivery');
        }

        // Reload order to get updated data
        const updatedOrder = await response.json();
        setOrder(updatedOrder);
    };

    // ── "Still missing" footer: resolve a missing-item key to a
    //    scroll target or route destination and give the user a
    //    one-click path to fix it. Called by ``MissingItemLink``
    //    below. Each key maps to one UI element the customer owns:
    //      * title / catalogue_number → scroll to the field grid and
    //        focus the specific input
    //      * product_configuration    → open the configurator page
    //      * shipping_address         → scroll to the delivery card
    //      * billing_profile          → navigate to /billing (opens
    //                                   in a NEW TAB so the order
    //                                   edits are not lost; on return
    //                                   the ``visibilitychange``
    //                                   listener auto-refreshes
    //                                   ``organization`` + order so
    //                                   the missing item disappears).
    const scrollToId = (id, focusId = null) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Brief outline flash so the destination is obvious.
        el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'rounded-md', 'transition');
        setTimeout(() => {
            el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2');
        }, 1600);
        if (focusId) {
            const input = document.getElementById(focusId);
            if (input) setTimeout(() => input.focus({ preventScroll: true }), 350);
        }
    };
    const handleMissingItemClick = (key) => {
        switch (key) {
            case 'title':
                scrollToId('section-order-details', 'order-title-field');
                break;
            case 'catalogue_number':
                scrollToId('section-order-details', 'catalogue-number');
                break;
            case 'product_configuration':
                navigate(`/orders/${orderId}/product?edit=true`);
                break;
            case 'shipping_address':
                scrollToId('section-shipping');
                break;
            case 'billing_profile':
                // Open in a new tab so the customer can finish billing
                // and return without losing any in-page edits. The
                // visibility listener below picks up the completed
                // profile when they switch back.
                window.open('/billing', '_blank', 'noopener');
                break;
            default:
                break;
        }
    };

    // Keep validation fresh when the customer returns from an
    // out-of-page detour (e.g. completing the billing profile in a
    // sibling tab). Re-pulls both the organization doc (billing
    // profile completeness lives there) and the order doc (shipping,
    // product configuration can also be edited elsewhere).
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const onVisibility = () => {
            if (document.visibilityState !== 'visible') return;
            // Re-fetch order (shipping, title, cat#, config) …
            fetchData();
            // … and re-pull organization so ``billing_profile_complete``
            // reflects any changes made on /billing.
            const orgId = user?.active_company_id || user?.organization_id;
            if (orgId) {
                const token = localStorage.getItem('token');
                fetch(`${API_URL}/api/organizations/${orgId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }).then(r => r.ok ? r.json() : null).then(data => {
                    if (data) setOrganization(data);
                }).catch(() => {});
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onVisibility);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, user?.active_company_id, user?.organization_id]);

    // Compact clickable rendering of the "Still missing: …" list.
    // Returns a sequence of underlined links separated by commas
    // (one per item), each wired to ``handleMissingItemClick``. Kept
    // inline (not extracted) because it closes over ``t`` and
    // ``handleMissingItemClick`` and is only used in two sibling
    // Submit cards.
    const renderMissingItems = () => (
        <>
            {submissionMissingItems.map((item, idx) => (
                <span key={item.key}>
                    <button
                        type="button"
                        onClick={() => handleMissingItemClick(item.key)}
                        className="underline underline-offset-2 decoration-amber-500/60 hover:decoration-amber-700 hover:text-amber-800 font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-sm"
                        data-testid={`missing-item-${item.key}`}
                    >
                        {item.label}
                    </button>
                    {idx < submissionMissingItems.length - 1 && <span>, </span>}
                </span>
            ))}
            <span>.</span>
        </>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <LoadingSpinner size="lg" />
                </div>
            </div>
        );
    }

    if (!order) return null;

    // Check if this is a simple media order (CD/DVD/Blu-ray)
    const isSimpleMedia = ['cd', 'dvd', 'bluray'].includes(order?.product_type);
    const isCd = order?.product_type === 'cd';
    const isReorder = order?.order_type === 'reorder';
    const productTypeLabel = {
        cd: 'CD',
        dvd: 'DVD',
        bluray: 'Blu-ray'
    }[order?.product_type] || order?.product_type;

    // Guest upload-link scoping: hide every section that doesn't match the
    // upload link's `allowed_categories`. Without this the guest sees the
    // full Production Files page (product config, price, tracklist, rights,
    // quote actions, etc.) which leaks order information beyond what the
    // upload link grants. See bug report 2026-05-27.
    const isGuest = user?.role === 'guest';
    const guestCategories = user?.guest_scope?.allowed_categories || [];
    const guestAllowsArtwork = !isGuest || guestCategories.includes('artwork');
    const guestAllowsMaster = !isGuest || guestCategories.some(
        (c) => c === 'audio' || c === 'master' || c.startsWith('audio_')
    );

    // ─── Extracted JSX building blocks ────────────────────────────────
    // Both tabs and the legacy (guest/reorder) layout share the same field
    // grid + artwork block. Defining them as local JSX consts keeps the
    // render tree readable without prop-drilling into yet another component.
    const ORDER_DETAIL_FIELDS_GRID = (
        <Card>
            <CardContent className="pt-6">
                <TooltipProvider delayDuration={150}>
                    {isSimpleMedia && (
                        <p className="text-sm text-slate-500 flex items-center gap-2 mb-4">
                            {order.product_type === 'cd' && <MediaDiscIcon type="cd" size={16} />}
                            {order.product_type === 'dvd' && <MediaDiscIcon type="dvd" size={16} />}
                            {order.product_type === 'bluray' && <MediaDiscIcon type="bluray" size={16} />}
                            {productTypeLabel} {t('order.production_label', 'Production')}
                            {order.quantity && ` • ${order.quantity.toLocaleString()} ${t('order.units', 'units')}`}
                        </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="order-detail-fields">
                        <div>
                            <Label htmlFor="order-title-field" className="text-xs font-medium text-slate-500 mb-1 block">
                                {t('order.title', 'Title')} <span className="text-red-400">*</span>
                            </Label>
                            <div className="relative">
                                <Input
                                    id="order-title-field"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    onBlur={(e) => handleSaveOrderField('title', e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    placeholder={t('order.ph_title', 'Enter release title')}
                                    className={`h-9 text-sm pr-8 placeholder:text-slate-400 ${canEditOrderDetails && !(editTitle || order.title || '').trim() ? 'border-amber-300 bg-amber-50/60 focus-visible:ring-amber-400' : ''}`}
                                    disabled={!canEditOrderDetails || savingField === 'title'}
                                    data-testid="order-title-edit"
                                />
                                {savingField === 'title' && (
                                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                                )}
                            </div>
                            {canEditOrderDetails && !(editTitle || order.title || '').trim() ? (
                                <p className="text-[11px] text-amber-600 font-medium mt-1" data-testid="title-required-hint">{t('order.required_to_submit', 'Required to submit')}</p>
                            ) : (
                                <p className="text-[11px] text-slate-400 mt-1">{t('order.required', 'Required')}</p>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="order-artist-field" className="text-xs font-medium text-slate-500 mb-1 block">
                                {t('order.artist', 'Artist')}
                            </Label>
                            <div className="relative">
                                <Input
                                    id="order-artist-field"
                                    value={editArtist}
                                    onChange={(e) => setEditArtist(e.target.value)}
                                    onBlur={(e) => handleSaveOrderField('artist', e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    placeholder={t('order.ph_artist', 'Enter artist name')}
                                    className="h-9 text-sm pr-8 placeholder:text-slate-400"
                                    disabled={!canEditOrderDetails || savingField === 'artist'}
                                    data-testid="order-artist-edit"
                                />
                                {savingField === 'artist' && (
                                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                                )}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1">{t('order.optional', 'Optional')}</p>
                        </div>
                        <div>
                            <Label htmlFor="catalogue-number" className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                                <span>{t('order.catalogue_number_short', 'Catalogue number')} <span className="text-red-400">*</span></span>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            className="text-slate-300 hover:text-slate-500 transition-colors"
                                            aria-label={t('order.what_is_this', 'What is this?')}
                                            data-testid="catalogue-info-tooltip"
                                        >
                                            <Info className="h-3 w-3" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                                        {t('order.catalogue_tooltip', 'Use your own catalogue number or release reference, e.g. CAT-001. We use it for rights clearance and production tracking.')}
                                    </TooltipContent>
                                </Tooltip>
                            </Label>
                            <div className="relative">
                                <Input
                                    id="catalogue-number"
                                    value={editCatalogueNumber}
                                    onChange={(e) => setEditCatalogueNumber(e.target.value)}
                                    onBlur={(e) => handleSaveCatalogueNumber(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    placeholder={t('order.ph_catalogue', 'Enter catalogue number')}
                                    className={`h-9 text-sm pr-8 placeholder:text-slate-400 ${canEditOrderDetails && !(editCatalogueNumber || order.catalogue_number || '').trim() ? 'border-amber-300 bg-amber-50/60 focus-visible:ring-amber-400' : ''}`}
                                    disabled={!canEditOrderDetails || isSavingCatalogueNumber}
                                    data-testid="catalogue-number-input"
                                />
                                {isSavingCatalogueNumber && (
                                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                                )}
                            </div>
                            {canEditOrderDetails && !(editCatalogueNumber || order.catalogue_number || '').trim() ? (
                                <p className="text-[11px] text-amber-600 font-medium mt-1" data-testid="catalogue-required-hint">{t('order.required_to_submit', 'Required to submit')}</p>
                            ) : (
                                <p className="text-[11px] text-slate-400 mt-1">{t('order.required_rights', 'Required for rights clearance')}</p>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="order-po-field" className="text-xs font-medium text-slate-500 mb-1 block">
                                {t('order.po_number', 'Purchase order number')}
                            </Label>
                            <div className="relative">
                                <Input
                                    id="order-po-field"
                                    value={editPoNumber}
                                    onChange={(e) => setEditPoNumber(e.target.value)}
                                    onBlur={(e) => handleSaveOrderField('customer_po_number', e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    placeholder={t('order.ph_po', 'Enter PO number')}
                                    className="h-9 text-sm pr-8 placeholder:text-slate-400"
                                    disabled={!canEditOrderDetails || savingField === 'customer_po_number'}
                                    data-testid="order-po-edit"
                                />
                                {savingField === 'customer_po_number' && (
                                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                                )}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1">{t('order.po_hint', 'Optional — for invoicing only')}</p>
                        </div>
                        <div>
                            <Label htmlFor="order-delivery-field" className="text-xs font-medium text-slate-500 mb-1 block">
                                {t('order.requested_delivery', 'Requested delivery date')}
                            </Label>
                            <div className="relative">
                                <Input
                                    id="order-delivery-field"
                                    type="date"
                                    value={editRequestedDelivery}
                                    onChange={(e) => setEditRequestedDelivery(e.target.value)}
                                    onBlur={(e) => handleSaveOrderField('requested_delivery_date', e.target.value)}
                                    className="h-9 text-sm pr-8"
                                    disabled={!canEditOrderDetails || savingField === 'requested_delivery_date'}
                                    data-testid="order-delivery-edit"
                                />
                                {savingField === 'requested_delivery_date' && (
                                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                                )}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1">{t('order.delivery_hint', 'Optional target date')}</p>
                        </div>
                    </div>

                    {/* Rights clearance status — contextual to the catalogue
                        number above (which is what gates clearance). Kept
                        inline as a single subtle row so the card stays short. */}
                    {!['cd', 'dvd', 'bluray'].includes(order?.product_type) && (() => {
                        const ncbStatus = order?.ncb_clearance?.status;
                        const isCleared = ncbStatus === 'cleared';
                        return (
                            <div className="mt-4 flex items-center gap-1.5 text-xs" data-testid="rights-clearance-inline">
                                <span className="text-slate-500">{t('order.rights_clearance', 'Rights clearance:')}</span>
                                <span className={`inline-flex items-center gap-1 font-medium ${isCleared ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    {isCleared ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                                    {isCleared ? t('order.rights_complete', 'Complete') : t('order.rights_progress', 'In progress')}
                                </span>
                            </div>
                        );
                    })()}
                </TooltipProvider>
            </CardContent>
        </Card>
    );


    // Inner Artwork body (rejection banner + files grid + upload
    // buttons) — reused by BOTH the legacy Card layout and the
    // collapsible layout so the two paths stay behaviourally identical.
    const ARTWORK_JOB_BODY = artworkJob ? (
                    <div className="space-y-4">
                        {artworkJob.artwork_status === 'rejected' && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="artwork-rejection-alert">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-red-900">{t('order.artwork_revision_needed', 'Artwork needs revision')}</h3>
                                        <p className="text-sm text-red-700 mt-1">
                                            {t('order.artwork_revision_desc', 'The repro partner has reviewed your artwork and found issues that need to be corrected. Click below to see the feedback and upload corrected files.')}
                                        </p>
                                        <Button
                                            size="sm"
                                            className="mt-3 bg-red-600 hover:bg-red-700"
                                            onClick={() => navigate(`/orders/${orderId}/artwork-review`)}
                                            data-testid="fix-artwork-btn"
                                        >
                                            {t('order.view_feedback_upload', 'View Feedback & Upload Correction')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="p-4 bg-purple-50/50 rounded-lg border border-purple-100">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium text-purple-800">
                                    {t('order.files_uploaded_count', '{n} file(s) uploaded', { n: displayArtworkFiles.filter(f => !f.superseded).length })}
                                </p>
                                {artworkJob.status === 'Awaiting_Approval' && (
                                    <Button
                                        size="sm"
                                        onClick={() => navigate(`/orders/${orderId}/artwork-review`)}
                                        className="bg-purple-600 hover:bg-purple-700"
                                        data-testid="review-artwork-btn"
                                    >
                                        <Eye className="h-4 w-4 mr-1" />
                                        {t('order.review_approve', 'Review & Approve')}
                                    </Button>
                                )}
                            </div>
                            {displayArtworkFiles.filter(f => !f.superseded).length > 0 && (
                                <ArtworkFilesGrid
                                    files={displayArtworkFiles.filter(f => !f.superseded)}
                                    allFiles={displayArtworkFiles}
                                    maxDisplay={6}
                                    onDelete={handleDeleteCustomerArtworkFile}
                                    canDelete={canDeleteFiles}
                                    jobLocked={['In_Progress', 'Awaiting_Approval', 'Approved', 'Completed'].includes(artworkJob?.status)}
                                />
                            )}
                        </div>
                        {canUploadFiles && ['Open', 'Assigned'].includes(artworkJob.status) && (
                            <Button
                                variant="outline"
                                onClick={() => setIsArtworkUploadDialogOpen(true)}
                                className="w-full"
                                data-testid="add-artwork-btn"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                {t('order.add_more_artwork_files', 'Add More Artwork Files')}
                            </Button>
                        )}
                    </div>
                ) : displayArtworkFiles.length > 0 ? (
                    <div className="space-y-4">
                        <div className="p-4 bg-purple-50/50 rounded-lg border border-purple-100">
                            <p className="text-sm font-medium text-purple-800 mb-3">
                                {t('order.files_uploaded_count', '{n} file(s) uploaded', { n: displayArtworkFiles.length })}
                            </p>
                            <ArtworkFilesGrid
                                files={displayArtworkFiles}
                                maxDisplay={6}
                                onDelete={handleDeleteCustomerArtworkFile}
                                canDelete={canDeleteFiles}
                            />
                            <p className="text-xs text-slate-500 mt-3">
                                {t('order.artwork_review_later', 'Your artwork will be reviewed after you submit the order.')}
                            </p>
                        </div>
                        {canUploadFiles && (
                            <Button
                                variant="outline"
                                onClick={() => setIsArtworkUploadDialogOpen(true)}
                                className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
                                data-testid="add-more-artwork-btn"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                {t('order.add_more_artwork', 'Add More Artwork')}
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="text-center py-8 bg-background rounded-lg mb-4">
                            <Palette className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                            <p className="text-sm text-slate-500">{t('order.no_artwork', 'No artwork uploaded yet')}</p>
                        </div>
                        {canUploadFiles && (
                            <Button
                                variant="outline"
                                onClick={() => setIsArtworkUploadDialogOpen(true)}
                                className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
                                data-testid="upload-artwork-btn"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                {t('order.upload_artwork', 'Upload Artwork')}
                            </Button>
                        )}
                    </>
                );

    // Inner Artwork content — reused inside our own <Card> shell (the
    // legacy layout, ARTWORK_SECTION_BLOCK below) and directly inside
    // <CollapsibleProductionSection> for the tabbed layout. Extracting
    // this keeps the two paths in lockstep: every artwork upload /
    // preview / rejection banner behaviour is defined ONCE.
    const ARTWORK_SECTION_INNER = (
        <div className="px-6 pb-6 pt-4">
            {artworkJob && (
                <div className="flex justify-end mb-3" data-testid="artwork-job-status-inline">
                    <ArtworkJobStatusBadge status={artworkJob.status} artworkStatus={artworkJob.artwork_status} />
                </div>
            )}
            {/* Reference templates resolved from the SKUs on this order —
                falls back to product_type when SKUs haven't been priced
                yet. Downloading a template is NOT an upload. */}
            <OrderArtworkTemplates orderId={orderId} />
            {ARTWORK_JOB_BODY}
        </div>
    );

    const ARTWORK_SECTION_BLOCK = (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Palette className="h-5 w-5 text-purple-500" />
                            Artwork
                        </CardTitle>
                        <CardDescription>
                            {isSimpleMedia
                                ? t('order.artwork_desc_media', 'Upload cover artwork and packaging files')
                                : t('order.artwork_desc_vinyl', 'Upload your artwork files for cover design')}
                        </CardDescription>
                    </div>
                    {artworkJob && (
                        <ArtworkJobStatusBadge status={artworkJob.status} artworkStatus={artworkJob.artwork_status} />
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {/* Reference templates resolved from the SKUs on this order —
                    falls back to product_type when SKUs haven't been priced
                    yet. Downloading a template is NOT an upload. */}
                <OrderArtworkTemplates orderId={orderId} />
                {ARTWORK_JOB_BODY}
            </CardContent>
        </Card>
    );

    return (
        <div className="min-h-screen bg-background" data-testid="order-files-page">
            <Header />
            
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* NOTE: <QuoteSection> was previously rendered here at the
                    top of the page. It now lives directly below the
                    Product Configuration card (see the two
                    `#section-product-config` wrappers further down) so
                    the quote request/approval UI sits next to the
                    configuration it refers to. Do NOT re-add a QuoteSection
                    here — that would duplicate the quote banner. */}

                {/* Payment Status Banners (shown above tabs so they're always visible) */}
                {paymentStatus === 'polling' && (
                    <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50 p-4 flex items-center gap-3" data-testid="payment-polling">
                        <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                        <div>
                            <p className="text-sm font-semibold text-indigo-900">{t('order.verifying_payment', 'Verifying payment...')}</p>
                            <p className="text-xs text-indigo-600">{t('order.verifying_payment_desc', 'Please wait while we confirm your payment.')}</p>
                        </div>
                    </div>
                )}
                {paymentStatus === 'paid' && (
                    <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="payment-success">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <div>
                            <p className="text-sm font-semibold text-emerald-900">{t('order.payment_received', 'Payment received')}</p>
                            <p className="text-xs text-emerald-600">{t('order.payment_received_desc', 'Your order is now being processed. No further action required.')}</p>
                        </div>
                    </div>
                )}
                {order.status === 'awaiting_payment' && paymentStatus !== 'polling' && paymentStatus !== 'paid' && (
                    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3" data-testid="payment-awaiting">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        <div>
                            <p className="text-sm font-semibold text-amber-900">{t('order.payment_required', 'Payment required')}</p>
                            <p className="text-xs text-amber-600">{t('order.payment_required_desc', 'This order is awaiting payment. Click "Proceed to Payment" below to complete your order.')}</p>
                        </div>
                    </div>
                )}
                {order.status === 'Paid' && paymentStatus !== 'paid' && (
                    <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="payment-confirmed">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <div>
                            <p className="text-sm font-semibold text-emerald-900">{t('order.payment_confirmed', 'Payment confirmed')}</p>
                            <p className="text-xs text-emerald-600">{t('order.payment_confirmed_desc', 'Your order has been paid and is being prepared for production.')}</p>
                        </div>
                    </div>
                )}

                {/* Tabbed layout. Guests skip the tabs entirely — their view
                    is always upload-link-scoped so a tab strip would only
                    confuse them. Reorders also skip tabs because they have
                    no production-file upload step. */}
                {(!isGuest && !isReorder) ? (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                        <div>
                            <TabsList
                                className="grid w-full grid-cols-2 max-w-xl h-auto bg-transparent p-0 gap-3 rounded-none"
                                data-testid="order-page-tabs"
                            >
                                <TabsTrigger
                                    value="details"
                                    className="group cursor-pointer flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl border-2 border-border bg-white text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 data-[state=active]:border-slate-300 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                                    data-testid="tab-order-details"
                                >
                                    <span>{t('order.tab_details', 'Order details')}</span>
                                    {submissionMissingItems.length > 0 ? (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700"
                                            data-testid="tab-details-missing-badge"
                                        >
                                            <AlertTriangle className="h-3 w-3" />
                                            {t('order.n_missing', '{n} missing', { n: submissionMissingItems.length })}
                                        </span>
                                    ) : (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
                                            data-testid="tab-details-complete-badge"
                                        >
                                            <CheckCircle2 className="h-3 w-3" />
                                            {t('order.complete_badge', 'Complete')}
                                        </span>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger
                                    value="files"
                                    className="group cursor-pointer flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl border-2 border-border bg-white text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 data-[state=active]:border-slate-300 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                                    data-testid="tab-production-files"
                                >
                                    <span>{t('order.tab_files', 'Production files')}</span>
                                    {productionFilesStatus === 'complete' ? (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
                                            data-testid="tab-files-complete-badge"
                                        >
                                            <CheckCircle2 className="h-3 w-3" />
                                            {t('order.files_complete', 'Files complete')}
                                        </span>
                                    ) : productionFilesStatus === 'missing' ? (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700"
                                            data-testid="tab-files-missing-badge"
                                        >
                                            <AlertTriangle className="h-3 w-3" />
                                            {t('order.files_missing', 'Files missing')}
                                        </span>
                                    ) : (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
                                            data-testid="tab-files-added-badge"
                                        >
                                            <CheckCircle2 className="h-3 w-3" />
                                            {t('order.files_added', 'Files added')}
                                        </span>
                                    )}
                                </TabsTrigger>
                            </TabsList>
                        </div>

                        {/* ─── TAB 1: Order details ──────────────────── */}
                        <TabsContent value="details" className="space-y-6 mt-0">
                            {/* Active shipment tracking — hoisted to the top
                                of the Details tab when the order has
                                carrier tracking so the customer sees
                                "where is my order NOW" immediately. The
                                component self-hides if there's no
                                tracking payload. */}
                            {hasActiveShipmentTracking && (
                                <ShipmentTrackingSection order={order} />
                            )}

                            {/* Test Press — Phase 2 approve/reject surfaces
                                when the TP is in "arrived" state (= TP:
                                Del.). The TestPressCard owns the dialog +
                                comment capture; backend enforces reject-
                                requires-comment and sends the confirmation
                                email with a context-aware deep-link. Self-
                                hides for orders where TP is "not_required"
                                / absent. */}
                            {order?.test_press_status && order.test_press_status !== 'not_required' && (
                                <div id="test-press">
                                    <TestPressCard
                                        order={order}
                                        onUpdated={(updated) =>
                                            setOrder((prev) => ({ ...prev, ...(updated || {}) }))
                                        }
                                    />
                                </div>
                            )}

                            <div className="space-y-1">
                                <h2 className="text-xl font-semibold text-slate-900">{t('order.review_title', 'Review your order')}</h2>
                                <p className="text-sm text-slate-500 leading-snug max-w-2xl">
                                    {t('order.review_desc', 'Complete the required details before submitting. Production files can be added separately.')}
                                </p>
                            </div>

                            {/* Order detail fields grid (inline editable).
                                Wrapped so the "Still missing" footer can
                                scroll Title / Catalogue # back into view. */}
                            <div id="section-order-details">
                                {ORDER_DETAIL_FIELDS_GRID}
                            </div>

                            {/* Product Summary Card — same rich layout for
                                every family (Vinyl / CD / DVD / Blu-ray). Legacy
                                CD/DVD/Blu-ray orders without a config_flow_id
                                fall back to the vinyl_specs display path,
                                which still shows quantity, price, Edit and
                                Download Quote. */}
                            <div id="section-product-config">
                                <FlowDrivenProductSummary
                                    order={order}
                                    onEdit={() => navigate(`/orders/${orderId}/product?edit=true`)}
                                    canEdit={canEdit}
                                    onDownloadQuote={handleDownloadQuote}
                                    isDownloading={isDownloadingQuote}
                                />
                            </div>

                            {/* Quote workflow banner — placed directly below
                                Product Configuration so request/approval UI
                                sits next to the configuration it refers to.
                                Component self-hides unless status is Quote/
                                Quoted, so this is a no-op for normal orders. */}
                            <QuoteSection order={order} onUpdate={fetchData} />

                            {/* Delivery Card */}
                            {(order?.shipping_address || order?.delivery_addresses?.length > 0 || canEdit) && (
                                <div id="section-shipping">
                                    <SplitDeliveryCard
                                        order={order}
                                        onDeliveryUpdate={handleDeliveryUpdate}
                                        canEdit={canEdit}
                                    />
                                </div>
                            )}

                            {/* Shipment Tracking — OwnTune-branded card that
                                renders whenever Inbound Hub has pushed at least
                                one tracking-changed update matched to a
                                destination on this order. Placed BELOW the
                                delivery card so the customer sees "where is
                                my order going?" first (shipping address) then
                                "where is my order now?" underneath. Component
                                self-hides when `order.shipment_tracking` is
                                empty. No DSV link, no supplier-sensitive data —
                                just normalized progress + effective delivery
                                date.
                                NOTE: When active tracking exists, the card is
                                hoisted to the TOP of the tab (see the
                                conditional at the start of this TabsContent).
                                Rendering a second instance here would
                                duplicate the card, so only render inline
                                when there's no active tracking (component
                                still self-hides). */}
                            {!hasActiveShipmentTracking && (
                                <ShipmentTrackingSection order={order} />
                            )}

                            {/* Submit Section — anchored at the bottom of the Order details tab */}
                            {canEdit && (
                                <Card className={hasShippingConfirmed && submissionMissingItems.length === 0 ? 'border-emerald-200 bg-emerald-50/40' : ''}>
                                    <CardContent className="py-6">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                            <div className="min-w-0">
                                                {isPrivateAccount ? (
                                                    <>
                                                        <h3 className="font-semibold text-slate-900 mb-1">{t('order.ready_to_pay', 'Ready to pay?')}</h3>
                                                        {submissionMissingItems.length > 0 ? (
                                                            <p className="text-sm text-amber-600 flex items-start gap-1.5" data-testid="missing-items-footer">
                                                                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                                <span>{t('order.still_missing', 'Still missing:')} {renderMissingItems()}</span>
                                                            </p>
                                                        ) : (
                                                            <p className="text-sm text-slate-600">
                                                                {t('order.pay_desc', 'Payment is required before production starts. You will be redirected to a secure payment page.')}
                                                            </p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <h3 className="font-semibold text-slate-900 mb-1">{t('order.ready_to_submit', 'Ready to submit?')}</h3>
                                                        {submissionMissingItems.length > 0 ? (
                                                            <p className="text-sm text-amber-600 flex items-start gap-1.5" data-testid="missing-items-footer">
                                                                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                                <span>{t('order.still_missing', 'Still missing:')} {renderMissingItems()}</span>
                                                            </p>
                                                        ) : (
                                                            <p className="text-sm text-slate-600">
                                                                {t('order.submit_desc', "Once submitted, you won't be able to edit the order.")}
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <Button
                                                    variant="outline"
                                                    onClick={() => navigate('/dashboard')}
                                                    disabled={isSubmitting}
                                                    data-testid="save-as-draft-btn"
                                                >
                                                    {t('order.save_as_draft', 'Save as Draft')}
                                                </Button>
                                                <Button
                                                    onClick={handleSubmitOrder}
                                                    disabled={isSubmitting || submissionMissingItems.length > 0}
                                                    className={submissionMissingItems.length === 0 ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}
                                                    data-testid="submit-order-btn"
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                            {isPrivateAccount ? t('order.redirecting', 'Redirecting...') : t('order.submitting', 'Submitting...')}
                                                        </>
                                                    ) : isPrivateAccount ? (
                                                        <>
                                                            <Send className="h-4 w-4 mr-2" />
                                                            {t('order.proceed_payment', 'Proceed to Payment')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Send className="h-4 w-4 mr-2" />
                                                            {t('order.submit_order', 'Submit Order')}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </TabsContent>

                        {/* ─── TAB 2: Production files ──────────────── */}
                        <TabsContent value="files" className="space-y-6 mt-0">
                            {/* Active shipment tracking — same hoist as the
                                Details tab so a customer who lands on the
                                Files tab also sees the tracker up top. */}
                            {!isGuest && hasActiveShipmentTracking && (
                                <ShipmentTrackingSection order={order} />
                            )}

                            <div className="space-y-1">
                                <h2 className="text-xl font-semibold text-slate-900">{t('order.files_title', 'Upload production files')}</h2>
                                <p className="text-sm text-slate-500 leading-snug max-w-2xl">
                                    {t('order.files_desc', 'Upload your audio masters, tracklist and artwork, or create secure upload links for external partners. These files are required before production can start, but they can be added while the order is still in draft.')}
                                </p>
                            </div>

                            {/* Compact read-only order identity line so the
                                customer can confirm which release/order they
                                are uploading files to without switching to
                                the Order Details tab. The full card remains
                                under Order Details — this is intentionally
                                minimal (no edit affordances, no icons). */}
                            {(() => {
                                const title = (order?.title || '').trim();
                                const cat = (order?.catalogue_number || '').trim();
                                const ptLabel = ({
                                    vinyl: 'Vinyl',
                                    cd: 'CD',
                                    dvd: 'DVD',
                                    bluray: 'Blu-ray',
                                }[order?.product_type]) || order?.product_type || '';
                                const qty = Number(order?.quantity) > 0
                                    ? `${Number(order.quantity).toLocaleString()} ${t('order.units', 'units')}`
                                    : '';
                                const orderNo = order?.order_number ? `${t('order.order_number_prefix', 'Order')} #${order.order_number}` : '';
                                // Suppress the line entirely if we don't
                                // have at least a title or catalogue number
                                // (e.g. brand-new drafts) — avoids showing
                                // an empty bullet row.
                                if (!title && !cat && !orderNo) return null;
                                const line1 = [title, cat].filter(Boolean).join(' · ');
                                const line2 = [ptLabel, qty, orderNo].filter(Boolean).join(' · ');
                                return (
                                    <div
                                        className="-mt-1 text-xs leading-relaxed text-slate-500"
                                        data-testid="production-files-order-identity"
                                    >
                                        {line1 && (
                                            <div className="font-medium text-slate-700" data-testid="production-files-identity-line-1">
                                                {line1}
                                            </div>
                                        )}
                                        {line2 && (
                                            <div data-testid="production-files-identity-line-2">
                                                {line2}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* External upload-link invitation — slim, compact
                                action panel hoisted directly below the page
                                introduction. Creating a link is a sharing
                                action only — it NEVER counts as a completed
                                file upload (the master/artwork summaries read
                                from uploaded `files`, not from links). */}
                            <div data-testid="upload-invite-card">
                                <UploadLinksSection orderId={orderId} productType={order?.product_type} compact />
                            </div>

                            {/* Expand / Collapse all controls — pure UI
                                state, no data or upload side-effects. */}
                            <div className="flex items-center justify-end gap-2 -mb-2">
                                <button
                                    type="button"
                                    onClick={() => setExpandAllSignal((n) => n + 1)}
                                    className="text-xs font-medium text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline transition-colors"
                                    data-testid="expand-all-sections-btn"
                                >
                                    {t('order.expand_all', 'Expand all')}
                                </button>
                                <span className="text-slate-300">·</span>
                                <button
                                    type="button"
                                    onClick={() => setCollapseAllSignal((n) => n + 1)}
                                    className="text-xs font-medium text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline transition-colors"
                                    data-testid="collapse-all-sections-btn"
                                >
                                    {t('order.collapse_all', 'Collapse all')}
                                </button>
                            </div>

                            {/* Master file rejection banner */}
                            <CustomerMasterRejectionBanner
                                order={order}
                                files={files}
                                onReplaced={fetchData}
                            />

                            {/* Audio/Master Files Section */}
                            <CollapsibleProductionSection
                                id="master-files"
                                title={t('order.master_files_title', 'Master Files')}
                                icon={<Disc className="h-5 w-5 text-amber-500" />}
                                status={masterFilesSummary.status}
                                statusLabel={masterFilesSummary.label}
                                summary={masterFilesSummary.text}
                                defaultOpen={false}
                                persistKey={`pf:${orderId}:master-files`}
                                openSignal={expandAllSignal + (sectionOpenBump['master-files'] || 0)}
                                closeSignal={collapseAllSignal}
                            >
                                <MasterFilesCard
                                    order={order}
                                    files={files}
                                    orderId={orderId}
                                    canUploadFiles={canUploadFiles}
                                    canDeleteFiles={canDeleteFiles}
                                    isUploading={isUploading}
                                    setIsUploading={setIsUploading}
                                    setOrder={setOrder}
                                    setFiles={setFiles}
                                    onRefresh={fetchData}
                                    formatFileSize={formatFileSize}
                                    handleDownloadFile={handleDownloadFile}
                                    handleDeleteFile={handleDeleteFile}
                                    onActiveUploadsChange={handleMasterUploadsActiveChange}
                                    embeddedInCollapsible
                                />
                            </CollapsibleProductionSection>

                            {/* CD DDP × Booklet tracklist validation — only
                                rendered for CD orders; the inner card
                                self-hides for other product types so we
                                gate the collapsible wrapper too. */}
                            {isCdProduct && (
                                <CollapsibleProductionSection
                                    id="tracklist-validation"
                                    title={t('order.tracklist_title', 'Tracklist Validation')}
                                    icon={<FileText className="h-5 w-5 text-sky-500" />}
                                    summary={t('order.tracklist_summary', 'CD DDP master × printed booklet consistency check')}
                                    defaultOpen={true}
                                    persistKey={`pf:${orderId}:tracklist-validation`}
                                    openSignal={expandAllSignal}
                                    closeSignal={collapseAllSignal}
                                >
                                    <TracklistValidationCard
                                        order={order}
                                        orderId={orderId}
                                        isAdmin={isAdminUser}
                                        onRefresh={fetchData}
                                        embeddedInCollapsible
                                    />
                                </CollapsibleProductionSection>
                            )}

                            {/* Artwork Section */}
                            <CollapsibleProductionSection
                                id="artwork"
                                title={t('order.artwork_title', 'Artwork')}
                                icon={<Palette className="h-5 w-5 text-purple-500" />}
                                status={artworkSummary.status}
                                statusLabel={artworkSummary.label}
                                summary={artworkSummary.text}
                                defaultOpen={false}
                                persistKey={`pf:${orderId}:artwork`}
                                openSignal={expandAllSignal + (sectionOpenBump.artwork || 0)}
                                closeSignal={collapseAllSignal}
                            >
                                {ARTWORK_SECTION_INNER}
                            </CollapsibleProductionSection>
                        </TabsContent>
                    </Tabs>
                ) : (
                    /* Guests + Reorders — flat (no tabs) legacy layout */
                    <div className="space-y-6">
                        {/* Active shipment tracking — same hoist as the
                            tabbed layout above. Reorders routinely enter
                            production and hit the dispatch/delivery phase
                            just like a normal order; this ensures the
                            tracker shows at the top of their flat layout
                            when `shipment_tracking` is populated.
                            Guests are excluded from the tracker (same
                            policy as the lower instance below). */}
                        {!isGuest && hasActiveShipmentTracking && (
                            <ShipmentTrackingSection order={order} />
                        )}

                        <div id="section-order-details">
                            {ORDER_DETAIL_FIELDS_GRID}
                        </div>

                        {!isGuest && (
                            <div id="section-product-config">
                                <FlowDrivenProductSummary
                                    order={order}
                                    onEdit={isReorder ? undefined : () => navigate(`/orders/${orderId}/product?edit=true`)}
                                    canEdit={canEdit && !isReorder}
                                    onDownloadQuote={handleDownloadQuote}
                                    isDownloading={isDownloadingQuote}
                                />
                            </div>
                        )}

                        {/* Quote workflow banner (flat layout variant) —
                            renders directly below Product Configuration,
                            matching the tabbed layout above. Self-hides for
                            non-Quote statuses so there is no duplicate when
                            the order is not in the quote flow. */}
                        {!isGuest && (
                            <QuoteSection order={order} onUpdate={fetchData} />
                        )}

                        {!isGuest && isReorder && (
                            <Card className="border-sky-200 bg-sky-50/50" data-testid="reorder-info-banner">
                                <CardContent className="py-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center shrink-0 mt-0.5">
                                            <CheckCircle2 className="h-4 w-4 text-sky-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-900 text-sm">{t('order.reorder_banner_title', 'Reorder — using existing production setup')}</h3>
                                            <p className="text-sm text-slate-500 mt-0.5">
                                                {t('order.reorder_banner_desc', 'Masters and artwork from the original order will be reused. No production files need to be uploaded again.')}
                                            </p>
                                            {order.catalogue_number && (
                                                <p className="text-xs text-slate-400 mt-1">{t('order.catalogue_prefix', 'Catalogue:')} {order.catalogue_number}</p>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {!isGuest && !isReorder && !['cd', 'dvd', 'bluray'].includes(order?.product_type) && (
                            <NCBCustomerStatus order={order} />
                        )}

                        {!isReorder && guestAllowsMaster && (
                            <CustomerMasterRejectionBanner
                                order={order}
                                files={files}
                                onReplaced={fetchData}
                            />
                        )}

                        {!isReorder && guestAllowsMaster && (
                            <MasterFilesCard
                                order={order}
                                files={files}
                                orderId={orderId}
                                canUploadFiles={canUploadFiles}
                                canDeleteFiles={canDeleteFiles}
                                isUploading={isUploading}
                                setIsUploading={setIsUploading}
                                setOrder={setOrder}
                                setFiles={setFiles}
                                onRefresh={fetchData}
                                formatFileSize={formatFileSize}
                                handleDownloadFile={handleDownloadFile}
                                handleDeleteFile={handleDeleteFile}
                            />
                        )}

                        {!isReorder && guestAllowsMaster && (
                            <TracklistValidationCard
                                order={order}
                                orderId={orderId}
                                isAdmin={isAdminUser}
                                onRefresh={fetchData}
                            />
                        )}

                        {!isReorder && guestAllowsArtwork && ARTWORK_SECTION_BLOCK}

                        {!isGuest && (order?.shipping_address || order?.delivery_addresses?.length > 0 || canEdit) && (
                            <SplitDeliveryCard
                                order={order}
                                onDeliveryUpdate={handleDeliveryUpdate}
                                canEdit={canEdit}
                            />
                        )}

                        {/* Shipment Tracking — same component as the Details
                            tab (self-hides when no tracking data). When
                            active tracking exists it is hoisted to the TOP
                            of this tab (see conditional at the start of
                            TabsContent). Keep the inline instance ONLY for
                            the no-tracking case so the component still
                            self-hides without duplicating the card. */}
                        {!isGuest && !hasActiveShipmentTracking && (
                            <ShipmentTrackingSection order={order} />
                        )}

                        {/* Submit Section for reorders — same UX as the
                            non-reorder "Ready to submit?" footer inside the
                            Order details tab. Reorders skip the tabs (no
                            production-file upload step) so we anchor the
                            submit card here as the final element. */}
                        {!isGuest && isReorder && canEdit && (
                            <Card
                                className={hasShippingConfirmed && submissionMissingItems.length === 0 ? 'border-emerald-200 bg-emerald-50/40' : ''}
                                data-testid="reorder-submit-card"
                            >
                                <CardContent className="py-6">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                        <div className="min-w-0">
                                            {isPrivateAccount ? (
                                                <>
                                                    <h3 className="font-semibold text-slate-900 mb-1">{t('order.ready_to_pay', 'Ready to pay?')}</h3>
                                                    {submissionMissingItems.length > 0 ? (
                                                        <p className="text-sm text-amber-600 flex items-start gap-1.5" data-testid="missing-items-footer-reorder">
                                                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                            <span>{t('order.still_missing', 'Still missing:')} {renderMissingItems()}</span>
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-slate-600">
                                                            {t('order.pay_desc', 'Payment is required before production starts. You will be redirected to a secure payment page.')}
                                                        </p>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <h3 className="font-semibold text-slate-900 mb-1">{t('order.ready_to_submit', 'Ready to submit?')}</h3>
                                                    {submissionMissingItems.length > 0 ? (
                                                        <p className="text-sm text-amber-600 flex items-start gap-1.5" data-testid="missing-items-footer-reorder">
                                                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                            <span>{t('order.still_missing', 'Still missing:')} {renderMissingItems()}</span>
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-slate-600">
                                                            {t('order.submit_desc', "Once submitted, you won't be able to edit the order.")}
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Button
                                                variant="outline"
                                                onClick={() => navigate('/dashboard')}
                                                disabled={isSubmitting}
                                                data-testid="reorder-save-as-draft-btn"
                                            >
                                                {t('order.save_as_draft', 'Save as Draft')}
                                            </Button>
                                            <Button
                                                onClick={handleSubmitOrder}
                                                disabled={isSubmitting || submissionMissingItems.length > 0}
                                                className={submissionMissingItems.length === 0 ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}
                                                data-testid="reorder-submit-order-btn"
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                        {isPrivateAccount ? t('order.redirecting', 'Redirecting...') : t('order.submitting', 'Submitting...')}
                                                    </>
                                                ) : isPrivateAccount ? (
                                                    <>
                                                        <Send className="h-4 w-4 mr-2" />
                                                        {t('order.proceed_payment', 'Proceed to Payment')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="h-4 w-4 mr-2" />
                                                        {t('order.submit_order', 'Submit Order')}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}
            </main>
            <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('order.upload_audio_title', 'Upload Audio File')}</DialogTitle>
                        <DialogDescription>
                            {t('order.upload_audio_desc', 'Upload your audio master file (WAV, AIFF, FLAC recommended)')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label>{t('order.select_file', 'Select File')}</Label>
                            <Input
                                type="file"
                                accept="audio/*,.wav,.aiff,.flac,.mp3"
                                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                ref={fileInputRef}
                                data-testid="audio-file-input"
                            />
                        </div>
                        
                        {selectedFile && (
                            <div className="p-3 bg-background rounded-lg flex items-center gap-3">
                                <Music className="h-5 w-5 text-blue-500" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                                    <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setIsUploadDialogOpen(false);
                                    setSelectedFile(null);
                                }}
                            >
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button
                                onClick={handleUploadFile}
                                disabled={isUploading || !selectedFile}
                                data-testid="confirm-upload-btn"
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        {t('order.uploading', 'Uploading...')}
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        {t('order.upload_btn', 'Upload')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Artwork Upload Dialog */}
            <Dialog open={isArtworkUploadDialogOpen} onOpenChange={setIsArtworkUploadDialogOpen}>
                <DialogContent className="max-w-lg overflow-hidden w-full">
                    <DialogHeader className="min-w-0">
                        <DialogTitle className="flex items-center gap-2">
                            <Palette className="h-5 w-5 text-purple-500" />
                            {t('order.upload_artwork', 'Upload Artwork')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('order.upload_artwork_desc', 'Upload your artwork files (images, PDFs) for review. Our team will process and prepare them for vinyl production.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4 min-w-0 w-full overflow-hidden">
                        {/* File input */}
                        <div className="space-y-2">
                            <Label>{t('order.select_files_label', 'Select Files')}</Label>
                            <div 
                                className="border-2 border-dashed border-purple-200 rounded-lg p-6 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors"
                                onClick={() => artworkInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={artworkInputRef}
                                    className="hidden"
                                    accept="image/*,.pdf,.ai,.eps,.psd"
                                    multiple
                                    onChange={handleArtworkFilesSelect}
                                    data-testid="artwork-file-input"
                                />
                                <ImageIcon className="h-10 w-10 mx-auto text-purple-400 mb-2" />
                                <p className="text-sm font-medium text-purple-600">{t('order.click_select_files', 'Click to select files')}</p>
                                <p className="text-xs text-slate-500 mt-1">{t('order.artwork_supports', 'Supports: JPG, PNG, PDF, AI, EPS, PSD')}</p>
                            </div>
                        </div>

                        {/* Selected files list */}
                        {artworkUploadFiles.length > 0 && (
                            <div className="space-y-2 min-w-0">
                                <Label>{t('order.selected_files', 'Selected Files ({n})', { n: artworkUploadFiles.length })}</Label>
                                <div className="max-h-40 overflow-y-auto overflow-x-hidden space-y-1 w-full min-w-0">
                                    {artworkUploadFiles.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between gap-2 p-2 bg-background rounded text-sm w-full min-w-0 overflow-hidden">
                                            <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                                                <FileImage className="h-4 w-4 text-purple-500 flex-shrink-0" />
                                                <span className="truncate min-w-0 max-w-full block flex-1" title={file.name}>{file.name}</span>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <span className="text-xs text-slate-400 whitespace-nowrap">{formatFileSize(file.size)}</span>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="h-6 w-6 p-0 flex-shrink-0"
                                                    onClick={() => removeArtworkFile(idx)}
                                                >
                                                    <Trash2 className="h-3 w-3 text-red-500" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Progress bar during upload */}
                        {isUploadingArtwork && artworkUploadProgress !== null && (
                            <div className="space-y-1.5" data-testid="artwork-upload-progress">
                                <div className="flex items-center justify-between text-xs text-slate-600">
                                    <span>{t('order.uploading_n_files', 'Uploading {n} file(s)...', { n: artworkUploadFiles.length })}</span>
                                    <span className="font-medium tabular-nums">{artworkUploadProgress}%</span>
                                </div>
                                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                                    <div
                                        className="h-full bg-purple-600 transition-all duration-200"
                                        style={{ width: `${artworkUploadProgress}%` }}
                                    />
                                </div>
                                {artworkUploadProgress === 100 && (
                                    <p className="text-xs text-slate-500 italic">{t('order.finalising', 'Finalising on server...')}</p>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setIsArtworkUploadDialogOpen(false);
                                    setArtworkUploadFiles([]);
                                }}
                                disabled={isUploadingArtwork}
                            >
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button
                                onClick={handleArtworkUpload}
                                disabled={isUploadingArtwork || artworkUploadFiles.length === 0}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid="confirm-artwork-upload-btn"
                            >
                                {isUploadingArtwork ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        {artworkUploadProgress !== null
                                            ? t('order.uploading_pct', 'Uploading {pct}%', { pct: artworkUploadProgress })
                                            : t('order.uploading', 'Uploading...')}
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        {t('order.upload_n_files', 'Upload {n} File(s)', { n: artworkUploadFiles.length })}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
